import maplibregl, {
  type FilterSpecification,
  type GeoJSONSourceSpecification,
  type Map as MLMap,
} from 'maplibre-gl';
import { Protocol as PMTilesProtocol } from 'pmtiles';
import type {
  EngineAdapter,
  Layer,
  LayerKind,
  VectorExportFeature,
  ViewerCamera,
  ViewerHostContext,
} from '@geosoft/viewer-sdk';
import { transformGeoJsonCoordinates } from '@/utils/vectorCoordinateOverride';
import {
  declareCapabilities,
  type EngineCapabilities,
  type LayerRenderDiagnostics,
} from '@/engines/contract';
import {
  clearMapLibreHostHandle,
  setMapLibreAdapterHandle,
  setMapLibreHostHandle,
} from '@/engines/maplibre/hostHandle';
import {
  applyIdentifyCursor,
  isHighlightActive,
  maplibreHighlightPaint,
  VECTOR_IDENTIFY_HIGHLIGHT,
  type VectorHighlightTarget,
} from '@/components/vector/vectorIdentifyHighlight';
import {
  POINT_SYMBOL_RASTER_PX,
  pointSymbolGeometryFor,
  pointSymbolRotationDeg,
  rasterizePointSymbol,
  rasterizeSvgPointIcon,
  type PointSymbolShape,
} from '@/components/vector/pointSymbolShape';
import {
  isPointIconId,
  loadPointIcons,
  peekPointIcon,
  pointIconDataUrl,
  type PointIconEntry,
} from '@/components/vector/pointIconRegistry';
import {
  createPolygonPatternCanvas,
  polygonPatternKey,
  type PolygonSymbolState,
} from '@/components/vector/polygonSymbolRegistry';
import type { UniqueValueSymbology } from '@/components/vector/vectorStyleModel';
import {
  lineDashArray,
  type LineSymbolState,
} from '@/components/vector/lineSymbolRegistry';
import {
  applyBasemap,
  loadPreferredBasemapId,
  savePreferredBasemapId,
  type BasemapId,
} from '@/engines/maplibre/basemaps';
import {
  applyOverlayEditCapture,
  editCursorForTool,
  resolveVectorEditSession,
  terraModeFromGeometry,
  type VectorEditTool,
} from '@/components/vector/vectorEditMode';
import type { IdentifyGeometryKind } from '@/components/vector/vectorIdentifyModel';
import {
  getVectorSessionFeaturesForLayer,
  type VectorEditResult,
} from '@/components/vector/vectorEditSession';
import {
  bboxFromGeometry,
  bboxRing,
  scaleGeometry,
  scaleHandlesFromBbox,
  type ScaleHandleId,
} from '@/components/vector/vectorScaleBox';
import {
  matchScaleFeature,
  pickFeatureAtLonLat,
  scaleFeaturesFrom,
  type ScaleSourceFeature,
} from '@/components/vector/scaleFeatureSource';
import { createLayerTerraDraw } from '@/engines/maplibre/vectorTerraDraw';
import type { TerraDraw } from 'terra-draw';
import { resolveCanvasLightColor, resolveVectorFallbackColor } from '@/design/tokenResolver';
import { normalizeTitilerSourceHost, ensureTitilerPngTileTemplate, withTitilerTileJsonPngFormat } from '@/utils/titilerSourceHost';
import {
  RASTER_TILE_GRID_SIZE,
  rasterResamplingExpression,
  requestTileSize,
  withDisplayTileSize,
} from '@/utils/rasterTileResolution';
import { resolveHillshadeTileUrl } from '@/utils/hillshade';
import { colorizeDemTileTemplate, isDemLikeLayer } from '@/utils/titilerDemColorize';
import {
  georeferencePreviewUrl,
  readAppliedGeoreferenceCorners,
} from '@/components/georeference/georeferencePreview';
import { readGeoreference } from '@/components/georeference/georeferenceTypes';
import { isLikelySingleBandRaster as isLikelySingleBandHint } from '@/utils/titilerRasterBands';
import { requestedBandCount } from '@/utils/titilerRasterProfile';
import {
  applyTitilerRasterStyleToTemplate,
  mapLibreRasterPaintFromState,
} from '@/components/raster/applyRasterToolCard';
import { ensureShadedReliefProtocol } from '@/components/raster/shadedReliefBlend';
import {
  ensureBackgroundMaskProtocol,
  wrapRasterTileTemplateForMapLibre,
} from '@/components/raster/backgroundMask';
import type { RasterColorRamp } from '@/state/useViewerStore';
import { assertHardwareWebGlForMapLibre, probeWebGlCapability } from '@/utils/webglCapability';
import { mercatorZoomForCameraHeight } from '@/engines/maplibre/overlayZoom';
import {
  catalogLayerIdFromMapLayerId,
  resolveFeatureIdFromRenderedFeature,
} from '@/engines/maplibre/maplibreVectorPick';
import {
  isContourPmtilesLayer,
  readDeclaredPmtilesSourceLayer,
  readPmtilesArchiveInfo,
  reconcilePmtilesSourceLayer,
} from '@/engines/pmtiles/sourceLayer';

const SUPPORTED: LayerKind[] = ['raster', 'vector'];

function isNonJsonVectorFeaturesUrl(url: string): boolean {
  try {
    const pathname = new URL(url, 'http://localhost').pathname.toLowerCase();
    return pathname.endsWith('.fgb')
      || pathname.endsWith('.parquet')
      || pathname.endsWith('.dxf');
  } catch {
    return /\.(fgb|parquet|dxf)(?:[?#]|$)/i.test(url);
  }
}

function layerUnsupportedReason(layer: Layer): string | undefined {
  const reason = layer.style?.viewerUnsupportedReason;
  return typeof reason === 'string' && reason.length > 0 ? reason : undefined;
}

type BboxFeatureCollection = {
  type: 'FeatureCollection';
  features: Array<{ geometry?: { coordinates?: unknown } | null }>;
};

const SCALE_GIZMO_SOURCE = 'src-scale-gizmo';
const SCALE_GIZMO_LINE = 'layer-scale-gizmo-line';
const SCALE_GIZMO_HANDLES = 'layer-scale-gizmo-handles';
const SCALE_EDIT_SOURCE = 'src-scale-edit';
const SCALE_EDIT_FILL = 'layer-scale-edit-fill';
const SCALE_EDIT_LINE = 'layer-scale-edit-line';

function emptyCollection(): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

const MAPLIBRE_CAPABILITIES: EngineCapabilities = declareCapabilities({
  engineId: 'maplibre',
  supportedLayerKinds: SUPPORTED,
  supportsTime: true,
  supportsTerrain: false,
  supports3DTiles: 'none',
  supportsInterleaving: ['deckgl'],
  defenceVariantSafe: true,
  pluginHostKinds: ['measurement', 'annotation', 'export'],
});

/** Register the `pmtiles://` protocol on MapLibre exactly once per process. */
let pmtilesProtocolRegistered = false;
export function ensurePmtilesProtocol(): void {
  if (pmtilesProtocolRegistered) return;
  const protocol = new PMTilesProtocol();
  maplibregl.addProtocol('pmtiles', protocol.tile);
  pmtilesProtocolRegistered = true;
  // ArcGIS Shaded Relief: colour ramp × hillshade blend.
  ensureShadedReliefProtocol(maplibregl);
  // ArcGIS background value: key the resampled collar out of each tile.
  ensureBackgroundMaskProtocol(maplibregl);
}

/**
 * MapLibre adapter - base map + raster overlays + simple vector layers + PMTiles.
 *
 * deck.gl can be interleaved on the same canvas later; for now we ship the
 * map only and mount deck.gl as an overlay in DeckGLAdapter.
 */
/** Extend a lon/lat bbox by walking nested GeoJSON coordinate arrays. */
function extendBboxFromCoords(
  bbox: [number, number, number, number] | null,
  coords: unknown,
): [number, number, number, number] | null {
  if (coords === null || coords === undefined) return bbox;
  if (Array.isArray(coords) && coords.length >= 2 && typeof coords[0] === 'number') {
    const x = coords[0] as number;
    const y = coords[1] as number;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return bbox;
    if (!bbox) return [x, y, x, y];
    return [
      Math.min(bbox[0], x),
      Math.min(bbox[1], y),
      Math.max(bbox[2], x),
      Math.max(bbox[3], y),
    ];
  }
  if (!Array.isArray(coords)) return bbox;
  let b = bbox;
  for (const c of coords) {
    b = extendBboxFromCoords(b, c);
  }
  return b;
}

/** Drop leftover MapLibre DOM (and their WebGL contexts) before remount. */
function purgeStaleMapLibreDom(container: HTMLElement): void {
  const wrappers = Array.from(container.querySelectorAll('.maplibregl-map'));
  const orphanCanvases = Array.from(container.querySelectorAll(':scope > canvas'));
  if (wrappers.length === 0 && orphanCanvases.length === 0) return;
  console.warn('[MapLibreAdapter] removing stale MapLibre DOM before mount', {
    wrappers: wrappers.length,
    orphanCanvases: orphanCanvases.length,
  });

  const loseCanvas = (canvas: HTMLCanvasElement) => {
    try {
      // Returns the existing context if MapLibre already created one.
      const gl = (
        canvas.getContext('webgl2')
        || canvas.getContext('webgl')
      ) as WebGLRenderingContext | null;
      gl?.getExtension('WEBGL_lose_context')?.loseContext();
    } catch {
      /* ignore */
    }
  };

  for (const wrapper of wrappers) {
    for (const canvas of Array.from(wrapper.querySelectorAll('canvas'))) {
      if (canvas instanceof HTMLCanvasElement) loseCanvas(canvas);
    }
    try {
      wrapper.remove();
    } catch {
      /* ignore */
    }
  }
  for (const canvas of orphanCanvases) {
    if (!(canvas instanceof HTMLCanvasElement)) continue;
    loseCanvas(canvas);
    try {
      canvas.remove();
    } catch {
      /* ignore */
    }
  }
}

function describeActiveWebGl(canvas: HTMLCanvasElement | null): Record<string, string | boolean | null> {
  if (!canvas) return { ok: false };
  try {
    const gl =
      canvas.getContext('webgl2', { failIfMajorPerformanceCaveat: false })
      || canvas.getContext('webgl', { failIfMajorPerformanceCaveat: false });
    if (!gl) return { ok: false };
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    return {
      ok: true,
      webgl2: typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext,
      vendor: dbg ? String(gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL)) : null,
      renderer: dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : null,
    };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

function logWebGlProbe(): void {
  const cap = probeWebGlCapability();
  if (!cap.ok) {
    console.error(
      '[MapLibreAdapter] WebGL probe FAILED / software GL — MapLibre would freeze this UI.',
      cap,
    );
    return;
  }
  console.info('[MapLibreAdapter] WebGL probe ok', cap);
}

/**
 * Convert a TiTiler XYZ tile template URL like
 *   `<base>/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=...&bidx=...`
 * into the matching `tilejson.json` URL so the source can read the COG's
 * real bounds and min/max zoom.
 */
function deriveTitilerTileJsonUrl(tilesUrl: string, preferTileSize?: number): string | null {
  const match = tilesUrl.match(/^(.*?)\/cog\/tiles\/([^/]+)\/\{z\}\/\{x\}\/\{y\}(?:\.[a-zA-Z]+)?(\?.*)?$/);
  if (!match) return null;
  const [, base, tileMatrixSet, query] = match;
  let tilejson = withTitilerTileJsonPngFormat(
    `${base}/cog/${tileMatrixSet}/tilejson.json${query ?? ''}`,
  );
  if (preferTileSize) {
    const sep = tilejson.includes('?') ? '&' : '?';
    if (!tilejson.includes('tilesize=')) tilejson = `${tilejson}${sep}tilesize=${preferTileSize}`;
  }
  return tilejson;
}


/**
 * TiTiler runs in Docker in local dev. When the embedded COG `url=` points to
 * `localhost`, TiTiler resolves that to the container itself and fails to read
 * converter artifacts. See `@/utils/titilerSourceHost`.
 */
// normalizeTitilerSourceHost imported from @/utils/titilerSourceHost

function isLikelySingleBandRaster(layer: Layer): boolean {
  return isLikelySingleBandHint({
    id: layer.id,
    kind: layer.kind,
    label: layer.label,
    title: typeof layer.style?.['title'] === 'string' ? layer.style['title'] : null,
    family: typeof layer.style?.['family'] === 'string' ? layer.style['family'] : null,
    artifactKind:
      typeof layer.style?.['gtwinArtifactKind'] === 'string'
        ? layer.style['gtwinArtifactKind']
        : typeof layer.style?.['artifactKind'] === 'string'
          ? layer.style['artifactKind']
          : null,
    terrainDerivative:
      typeof layer.style?.['terrainDerivative'] === 'string'
        ? layer.style['terrainDerivative']
        : null,
  });
}

function normalizeRasterBands(tilesUrl: string, layer: Layer): string {
  // Pre-network default only. An explicit multi-band template (converter RGB
  // output, or a template already corrected from `/cog/info`) is authoritative.
  if (requestedBandCount(tilesUrl) > 1) return tilesUrl;
  if (!isLikelySingleBandRaster(layer)) return tilesUrl;
  try {
    const url = new URL(tilesUrl);
    url.searchParams.delete('bidx');
    url.searchParams.append('bidx', '1');
    return url.toString().replace(/%7B/g, '{').replace(/%7D/g, '}');
  } catch {
    return tilesUrl.replace(/&bidx=2&bidx=3/g, '');
  }
}

function bboxFromGeoJsonFeatureCollection(
  data: BboxFeatureCollection,
): [number, number, number, number] | null {
  let bbox: [number, number, number, number] | null = null;
  for (const f of data.features ?? []) {
    const g = f.geometry;
    if (!g?.coordinates) continue;
    bbox = extendBboxFromCoords(bbox, g.coordinates);
  }
  return bbox;
}

/**
 * Construction options for `MapLibreAdapter`.
 *
 * `vectorOverlayMode` is used when the adapter is mounted **alongside**
 * Giro3D (engine = 'giro3d') purely to render vector / PMTiles layers
 * as a 2D overlay on top of the unified Giro3D scene. In this mode:
 *
 *   - The MapLibre canvas is positioned absolutely on top of the
 *     Giro3D canvas with `pointer-events: none` so 3D OrbitControls
 *     keep working.
 *   - The MapLibre canvas background is transparent and no basemap
 *     is mounted — Giro3D already paints the imagery basemap via its
 *     own Map entity.
 *   - `moveend` does NOT emit `cameraChange` events so it never
 *     fights Giro3D's primary camera.
 *
 * Trade-off: vectors are rendered in 2D top-down map projection and do
 * NOT drape on the 3D terrain. Rotating Giro3D's 3D camera leaves
 * vectors flat in screen space. Proper 3D-draped vectors require a
 * Giro3D vector ColorLayer (Wave-3 scope).
 */
export interface MapLibreAdapterOptions {
  readonly vectorOverlayMode?: boolean;
}

/**
 * What a point `symbol` layer draws: a built-in polygon rasterized from a
 * canvas path, or an SVG icon from the lazily-loaded registry.
 */
type PointSymbolSource =
  | { readonly kind: 'shape'; readonly shape: PointSymbolShape }
  | { readonly kind: 'icon'; readonly entry: PointIconEntry };

function cleanTerraDrawFeature(feature: GeoJSON.Feature): VectorExportFeature {
  const properties = { ...(feature.properties ?? {}) };
  delete properties.mode;
  delete properties.selected;
  return {
    id:
      typeof feature.id === 'string' || typeof feature.id === 'number'
        ? feature.id
        : undefined,
    properties,
    geometry: feature.geometry,
  };
}

function completedPolygonFeature(feature: GeoJSON.Feature): GeoJSON.Feature | null {
  const geometry = feature.geometry;
  if (!geometry) return null;
  if (geometry.type === 'Point') {
    return geometry.coordinates.length >= 2 ? feature : null;
  }
  if (geometry.type === 'LineString') {
    return geometry.coordinates.length >= 2 ? feature : null;
  }
  if (geometry.type === 'Polygon') {
    const rings = geometry.coordinates
      .map(closePolygonRing)
      .filter((ring): ring is GeoJSON.Position[] => ring != null);
    if (rings.length === 0) return null;
    return { ...feature, geometry: { ...geometry, coordinates: rings } };
  }
  if (geometry.type === 'MultiPolygon') {
    const polygons = geometry.coordinates
      .map((polygon) =>
        polygon
          .map(closePolygonRing)
          .filter((ring): ring is GeoJSON.Position[] => ring != null),
      )
      .filter((polygon) => polygon.length > 0);
    if (polygons.length === 0) return null;
    return { ...feature, geometry: { ...geometry, coordinates: polygons } };
  }
  return feature;
}

function closePolygonRing(ring: GeoJSON.Position[]): GeoJSON.Position[] | null {
  const valid = ring.filter(
    (position) =>
      Array.isArray(position)
      && position.length >= 2
      && Number.isFinite(position[0])
      && Number.isFinite(position[1]),
  );
  if (valid.length < 3) return null;
  const first = valid[0];
  const last = valid.at(-1);
  if (!last || first[0] !== last[0] || first[1] !== last[1]) {
    return [...valid, [...first]];
  }
  return valid;
}

export class MapLibreAdapter implements EngineAdapter {
  id = 'maplibre' as const;
  readonly capabilities: EngineCapabilities = MAPLIBRE_CAPABILITIES;
  private map: MLMap | null = null;
  private rasterLayerIds = new Set<string>();
  /** Last visibility requested per layer id, survives unmount/remount cycles. */
  private desiredVisibility = new Map<string, boolean>();
  /**
   * Live TiTiler XYZ template currently driving each raster source. Updated
   * when the Raster ToolCard rewrites bands / colormap / nodata so the next
   * apply starts from the previous URL rather than the catalog original.
   */
  private rasterTileTemplates = new Map<string, string>();
  /**
   * Cache of the GeoJSON features held by each `vector` layer the
   * adapter knows about. Populated by `addVectorFromFeaturesEndpoint`;
   * consumed by `getVectorFeatures` so the WP-4 attribute table can
   * render rows without re-fetching.
   */
  private vectorFeatureCache = new Map<string, GeoJSON.Feature[]>();
  /** Session working-copy features for immutable PMTiles/Giro3D vector sources. */
  private vectorEditOverlayFeatures = new Map<string, Map<string, GeoJSON.Feature>>();
  /** MapLibre style layer ids used by `queryRenderedFeatures` for vector picks. */
  private vectorPickLayerIds = new Set<string>();
  private containerClickHandler: ((event: MouseEvent) => void) | null = null;
  private hostContainer: HTMLElement | null = null;
  private hostContext: ViewerHostContext | null = null;
  private overlayResizeObserver: ResizeObserver | null = null;
  private vectorOverlayMode: boolean;
  private currentBasemapId: BasemapId = loadPreferredBasemapId();
  private readonly canvasLight = resolveCanvasLightColor();
  private readonly vectorFallback = resolveVectorFallbackColor();
  private lastVectorPickProperties: Readonly<Record<string, unknown>> | null = null;
  private vectorPaintById = new Map<
    string,
    {
      readonly fillColor?: string;
      readonly fillOpacity?: number;
      readonly lineColor?: string;
      readonly lineWidth?: number;
      readonly lineSymbol?: LineSymbolState;
      readonly pointSize?: number;
      readonly pointSymbolId?: string;
      readonly pointRotation?: number;
      readonly polygonSymbol?: PolygonSymbolState;
      readonly uniqueValueSymbology?: UniqueValueSymbology | null;
    }
  >();
  private readonly polygonPatternImageByLayer = new Map<string, string>();
  private readonly uniquePatternImagesByLayer = new Map<string, Set<string>>();
  private vectorHighlightById = new Map<string, VectorHighlightTarget>();
  private terraDraw: TerraDraw | null = null;
  private terraDrawOptions: { snap: boolean; scale: boolean } | null = null;
  private terraDrawLayerId: string | null = null;
  private terraDrawTool: VectorEditTool | null = null;
  private terraDrawSeedIds = new Set<string>();
  private terraDrawOnEdit: ((result: VectorEditResult) => void) | null = null;
  private scaleLayerId: string | null = null;
  private scaleFeature: GeoJSON.Feature | null = null;
  private scaleDragHandle: ScaleHandleId | null = null;
  private scaleListeners: Array<{ target: EventTarget; type: string; handler: EventListener }> = [];
  /**
   * Lon/lat geometry handed over by the Vector ToolCard. Giro3D twins route
   * vector layers away from MapLibre, so `queryRenderedFeatures` has nothing
   * to hit and this is the only source Scale can pick from.
   */
  private scaleFeatures: ScaleSourceFeature[] = [];
  /** Icon ids already uploaded via `addImage`, keyed by symbol + colour. */
  private readonly pointSymbolImageIds = new Set<string>();
  /** Icon ids whose SVG decode is in flight, so it is only started once. */
  private readonly pendingPointIconImages = new Set<string>();
  /** `layerId|symbolId` pairs already waiting on the lazy icon-set import. */
  private readonly pendingPointIconSets = new Set<string>();
  private readonly layerHealth = new Map<string, 'ready' | 'loading' | 'failed' | 'unsupported'>();
  /**
   * Outcome of PMTiles `source-layer` reconciliation per layer, so the live
   * diagnostics (Layer Properties) can surface a producer-side mismatch:
   * what the scene item DECLARED vs what was APPLIED vs what the archive
   * actually CONTAINS. Populated in `resolvePmtilesSourceLayer`.
   */
  private readonly pmtilesSourceLayerResolution = new Map<
    string,
    { declared: string | null; applied: string | null; available: readonly string[] }
  >();

  constructor(options: MapLibreAdapterOptions = {}) {
    this.vectorOverlayMode = options.vectorOverlayMode === true;
  }

  supports(kind: LayerKind): boolean {
    // Vector-overlay mode (used when Giro3D is the primary engine and
    // MapLibre is mounted only to render 2D vector overlays) must NOT
    // claim raster support — the adapter intentionally has no basemap
    // in that mode, so a raster layer routed here would be silently
    // dropped and the operator would see a blank background. Refusing
    // raster here lets ViewerHost.pickEngine fall back to the primary
    // adapter (Giro3D) which renders OSM via its Map entity.
    if (this.vectorOverlayMode && kind === 'raster') return false;
    return SUPPORTED.includes(kind);
  }

  async mount(host: ViewerHostContext): Promise<void> {
    const overlay = this.vectorOverlayMode;
    this.hostContainer = host.container;
    this.hostContext = host;

    // HMR / remounts can leave dead MapLibre canvases that exhaust the
    // browser's WebGL context budget (~8–16). Clear them before creating
    // a new context — common failure mode on Mesa/llvmpipe shared hosts.
    purgeStaleMapLibreDom(host.container);
    // Software WebGL (SwiftShader/llvmpipe) creates a context but freezes the
    // UI for seconds on zoom/pan (GPU stall / ReadPixels). Refuse it early so
    // TwinShell falls back to Canvas 2D OpenLayers.
    assertHardwareWebGlForMapLibre();
    logWebGlProbe();

    const mapOptionsBase = {
      container: host.container,
      style: {
        version: 8 as const,
        sources: {},
        layers: overlay
          ? []
          : [
              {
                id: 'background',
                type: 'background' as const,
                // Light empty canvas — dark canvas looked identical to a
                // failed map load, so operators could not tell tiles were missing.
                paint: { 'background-color': '#D9E2EC' },
              },
            ],
      },
      center: [55.27, 25.2] as [number, number],
      zoom: 9,
      maxZoom: 18,
      attributionControl: { compact: true } as const,
      // `preserveDrawingBuffer: true` breaks WebGL context creation on
      // Mesa/llvmpipe (ANGLE) — BindToCurrentSequence failed. Keep false.
      preserveDrawingBuffer: false,
      antialias: false,
      // Prefer failing fast over accepting software GL that freezes the tab.
      failIfMajorPerformanceCaveat: true,
      fadeDuration: 0,
      maxParallelImageRequests: 4,
      maxTileCacheSize: 64,
      refreshExpiredTiles: false,
      trackResize: true,
      // A raster source only holds a template, but TiTiler's hillshade needs
      // the tile's ground resolution to match QGIS (see utils/hillshade.ts).
      // This is the one place MapLibre exposes a resolved tile URL.
      transformRequest: (url: string) => {
        const resolved = resolveHillshadeTileUrl(url);
        return resolved === url ? undefined : { url: resolved };
      },
    };

    try {
      this.map = new maplibregl.Map(mapOptionsBase);
    } catch (firstErr) {
      console.warn(
        '[MapLibreAdapter] first WebGL mount failed; purging and retrying once',
        firstErr,
      );
      purgeStaleMapLibreDom(host.container);
      await new Promise((r) => setTimeout(r, 50));
      try {
        this.map = new maplibregl.Map(mapOptionsBase);
      } catch (secondErr) {
        console.error('[MapLibreAdapter] WebGL mount failed after retry', secondErr);
        throw secondErr;
      }
    }

    setMapLibreHostHandle(host.container, this.map);
    setMapLibreAdapterHandle(host.container, this);

    this.map.on('error', (event) => {
      const err = (event as { error?: Error }).error;
      console.error('[MapLibreAdapter] map/tile error', {
        message: err?.message ?? String(event),
        stack: err?.stack,
      });
    });

    if (!overlay) {
      this.map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
      this.map.addControl(new maplibregl.ScaleControl({ maxWidth: 120 }), 'bottom-left');
      this.currentBasemapId = loadPreferredBasemapId();
    }

    if (!overlay) {
      // In overlay-mode the primary 3D camera lives on Giro3D; emitting
      // cameraChange from MapLibre would fight it. Skip the listener.
      let camTimer: ReturnType<typeof setTimeout> | null = null;
      this.map.on('moveend', () => {
        if (!this.map) return;
        const c = this.map.getCenter();
        const camera = { lonLat: [c.lng, c.lat] as [number, number], zoom: this.map.getZoom() };
        // Coalesce store writes — TwinShell re-renders are expensive.
        if (camTimer) clearTimeout(camTimer);
        camTimer = setTimeout(() => {
          host.emit({ type: 'cameraChange', camera });
        }, 300);
      });
    }

    await new Promise<void>((resolve) => {
      this.map!.once('load', () => resolve());
    });

    console.info('[MapLibreAdapter] map mounted', {
      overlay,
      basemap: this.currentBasemapId,
      webgl: describeActiveWebGl(this.map.getCanvas()),
    });

    if (overlay) {
      // Vector-overlay mode: paint MapLibre canvas on top of Giro3D
      // with transparent background and pass-through pointer events.
      // The host container is shared with Giro3D, which mounts its own
      // canvas earlier; the MapLibre canvas is added later in the
      // sibling list, so it naturally sits above.
      //
      // Allow steep tilt so the overlay can follow oblique Giro3D poses
      // (syncOverlayCamera maps the 3D pitch into MapLibre's 0..maxPitch
      // range). Scoped to overlay mode so primary-engine UX is unchanged.
      try {
        this.map.setMaxPitch(85);
      } catch {
        /* older MapLibre without setMaxPitch — falls back to default cap */
      }
      const canvas = this.map.getCanvas();
      canvas.style.position = 'absolute';
      canvas.style.inset = '0';
      canvas.style.pointerEvents = 'none';
      canvas.style.background = 'transparent';
      canvas.style.zIndex = '10';
      const canvasParent = canvas.parentElement;
      if (canvasParent) {
        canvasParent.style.position = 'absolute';
        canvasParent.style.inset = '0';
        canvasParent.style.pointerEvents = 'none';
        canvasParent.style.background = 'transparent';
        canvasParent.style.zIndex = '10';
      }
      // The wrapper element MapLibre injects also needs the overlay
      // treatment so the canvas dimensions track the host.
      const wrapper = this.map.getContainer();
      wrapper.style.position = 'absolute';
      wrapper.style.inset = '0';
      wrapper.style.pointerEvents = 'none';
      wrapper.style.background = 'transparent';
      wrapper.style.zIndex = '10';
      this.map.resize();
      requestAnimationFrame(() => this.map?.resize());
      requestAnimationFrame(() => requestAnimationFrame(() => this.map?.resize()));
      if (typeof ResizeObserver !== 'undefined') {
        this.overlayResizeObserver = new ResizeObserver(() => {
          try {
            this.map?.resize();
          } catch {
            /* resize is best-effort */
          }
        });
        this.overlayResizeObserver.observe(host.container);
      }
      console.log('[MapLibreAdapter] mounted in vector-overlay mode (no basemap, pointer-events: none)');
      this.installVectorClickHandler(host);
    } else {
      // Apply the persisted basemap once the style is ready (primary mode only).
      try {
        applyBasemap(this.map!, this.currentBasemapId);
      } catch (err) {
        console.warn('[MapLibreAdapter] applyBasemap on mount failed', err);
      }
      this.map.on('click', (event) => {
        const hit = this.queryTopVectorFeature(event.point);
        if (hit) {
          host.emit({
            type: 'select',
            selection: {
              layerId: hit.catalogLayerId,
              featureId: hit.featureId,
              point: [event.lngLat.lng, event.lngLat.lat],
            },
          });
          return;
        }
        host.emit({
          type: 'select',
          selection: {
            layerId: '__map__',
            point: [event.lngLat.lng, event.lngLat.lat],
          },
        });
      });
    }
  }

  /** Active basemap id (persisted across reloads via localStorage). */
  getBasemapId(): BasemapId {
    return this.currentBasemapId;
  }

  /** Swap the basemap at runtime without disturbing user data layers. */
  setBasemap(id: BasemapId): void {
    if (!this.map) return;
    if (id === this.currentBasemapId) return;
    applyBasemap(this.map, id);
    this.currentBasemapId = id;
    savePreferredBasemapId(id);
  }

  async unmount(): Promise<void> {
    if (this.hostContainer && this.containerClickHandler) {
      this.hostContainer.removeEventListener('click', this.containerClickHandler, true);
    }
    this.containerClickHandler = null;
    this.hostContainer = null;
    this.hostContext = null;
    this.overlayResizeObserver?.disconnect();
    this.overlayResizeObserver = null;
    this.stopTerraDraw();
    this.stopScaleMode();
    const container = this.map?.getContainer();
    this.map?.remove();
    if (container) {
      clearMapLibreHostHandle(container);
    }
    this.map = null;
    this.rasterLayerIds.clear();
    this.vectorFeatureCache.clear();
    this.vectorEditOverlayFeatures.clear();
    this.vectorPickLayerIds.clear();
    this.layerHealth.clear();
    this.desiredVisibility.clear();
    this.rasterTileTemplates.clear();
    this.vectorPaintById.clear();
    this.polygonPatternImageByLayer.clear();
    this.uniquePatternImagesByLayer.clear();
    this.vectorHighlightById.clear();
    this.pointSymbolImageIds.clear();
    this.pendingPointIconImages.clear();
    this.pendingPointIconSets.clear();
  }

  /**
   * Mount a layer, then re-assert the last requested visibility.
   *
   * Raster mounts await TileJSON and TiTiler stats, so a hide can arrive while
   * the mount is still in flight. MapLibre creates style layers visible by
   * default, which used to leave the raster painted with the eye switched off.
   */
  async addLayer(layer: Layer): Promise<void> {
    try {
      await this.mountLayer(layer);
    } finally {
      if (this.desiredVisibility.get(layer.id) === false) {
        this.setLayerVisible(layer.id, false);
      }
    }
  }

  private async mountLayer(layer: Layer): Promise<void> {
    if (!this.map) throw new Error('MapLibreAdapter not mounted');
    this.layerHealth.set(layer.id, 'loading');
    if (!this.supports(layer.kind)) {
      this.layerHealth.set(layer.id, 'unsupported');
      return;
    }

    const unsupportedReason = layerUnsupportedReason(layer);
    if (unsupportedReason) {
      this.layerHealth.set(layer.id, 'unsupported');
      return;
    }

    const sourceId = `src-${layer.id}`;
    const layerId = `layer-${layer.id}`;

    // React dev strict-mode and scene sync retries can call addLayer for a
    // layer that MapLibre already has. MapLibre throws on duplicate source ids,
    // leaving stale raster tile templates in place. Treat add as an idempotent
    // replace so zoom/tilejson changes take effect after reloads or patches.
    this.removeMapLibreArtifacts(layer.id);

    // ---- VECTOR: GeoJSON / OGC Features URL ----
    if (layer.kind === 'vector' && layer.endpoints.features) {
      if (isNonJsonVectorFeaturesUrl(layer.endpoints.features)) {
        this.layerHealth.set(layer.id, 'unsupported');
        return;
      }
      try {
        await this.addVectorFromFeaturesEndpoint(sourceId, layerId, layer);
        this.layerHealth.set(layer.id, 'ready');
      } catch (err) {
        this.layerHealth.set(layer.id, 'failed');
        throw err;
      }
      return;
    }

    // ---- VECTOR PMTiles ----
    if (layer.kind === 'vector' && layer.endpoints.pmtiles) {
      try {
        ensurePmtilesProtocol();
        this.map.addSource(sourceId, {
          type: 'vector',
          url: `pmtiles://${layer.endpoints.pmtiles}`,
        });
        // Default style: outlined lines + soft fill. Real per-layer styling
        // arrives with the style editor in a later charter.
        const isContourDerivative = isContourPmtilesLayer(layer);
        const sourceLayer = await this.resolvePmtilesSourceLayer(layer, isContourDerivative);
        const color = (layer.style?.['color'] as string | undefined)
          ?? (isContourDerivative ? '#111827' : this.vectorFallback);
        console.info('[MapLibreAdapter] PMTiles mount', {
          layerId: layer.id,
          url: layer.endpoints.pmtiles,
          sourceLayer,
          color,
          bbox: layer.bbox,
          canvas: {
            width: this.map.getCanvas().clientWidth,
            height: this.map.getCanvas().clientHeight,
          },
        });
        if (!sourceLayer) {
          console.warn(
            `[MapLibreAdapter] PMTiles source layer missing for ${layer.id}; showing bbox fallback only`,
          );
          if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
          this.addVectorBboxFallback(sourceId, layerId, layer);
          this.layerHealth.set(layer.id, 'ready');
          return;
        }
        try {
          this.map.addLayer({
            id: `${layerId}-fill`,
            type: 'fill',
            source: sourceId,
            'source-layer': sourceLayer,
            minzoom: layer.visibility?.minZoom ?? 0,
            maxzoom: layer.visibility?.maxZoom ?? 22,
            filter: ['match', ['geometry-type'], ['Polygon', 'MultiPolygon'], true, false],
            paint: {
              'fill-color': color,
              'fill-opacity': isContourDerivative ? 0 : 0.24,
            },
          });
          this.registerVectorPickLayer(`${layerId}-fill`);
          this.map.addLayer({
            id: `${layerId}-line`,
            type: 'line',
            source: sourceId,
            'source-layer': sourceLayer,
            minzoom: layer.visibility?.minZoom ?? 0,
            maxzoom: layer.visibility?.maxZoom ?? 22,
            paint: {
              'line-color': color,
              'line-width': isContourDerivative ? 1 : 2,
              'line-opacity': isContourDerivative ? 0.85 : 1,
            },
          });
          this.registerVectorPickLayer(`${layerId}-line`);
          this.map.addLayer({
            id: `${layerId}-circle`,
            type: 'circle',
            source: sourceId,
            'source-layer': sourceLayer,
            minzoom: layer.visibility?.minZoom ?? 0,
            maxzoom: layer.visibility?.maxZoom ?? 22,
            filter: ['match', ['geometry-type'], ['Point', 'MultiPoint'], true, false],
            paint: {
              'circle-radius': 8,
              'circle-color': color,
              'circle-stroke-color': '#ffffff',
              'circle-stroke-width': 1,
            },
          });
          this.registerVectorPickLayer(`${layerId}-circle`);
          requestAnimationFrame(() => this.logVectorRenderState(`addLayer:${layer.id}`));
          this.fitBoundsForLayer(layer);
        } catch {
          if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
          this.addVectorBboxFallback(sourceId, layerId, layer);
        }
        this.layerHealth.set(layer.id, 'ready');
      } catch (err) {
        this.layerHealth.set(layer.id, 'failed');
        throw err;
      }
      return;
    }

    // ---- RASTER (XYZ template) ----
    if (layer.kind === 'raster' && layer.endpoints.tiles) {
      try {
        await this.addRasterTilesLayer(sourceId, layerId, layer);
        this.rasterLayerIds.add(layer.id);
        this.fitBoundsForLayer(layer);
        this.layerHealth.set(layer.id, 'ready');
      } catch (err) {
        this.layerHealth.set(layer.id, 'failed');
        throw err;
      }
      return;
    }

    // ---- VECTOR fallback: bbox rectangle (often invisible when bbox is global) ----
    if (layer.kind === 'vector' && layer.bbox) {
      try {
        this.addVectorBboxFallback(sourceId, layerId, layer);
        this.layerHealth.set(layer.id, 'ready');
      } catch (err) {
        this.layerHealth.set(layer.id, 'failed');
        throw err;
      }
      return;
    }

    // Unknown kind / no endpoints: ignore silently.
    this.layerHealth.set(layer.id, 'unsupported');
  }

  /**
   * Fetch `endpoints.features` (GeoJSON Feature( Collection ) or typical OGC API
   * Features JSON) and render fill + line layers.
   */
  private async addVectorFromFeaturesEndpoint(
    sourceId: string,
    layerId: string,
    layer: Layer,
  ): Promise<void> {
    if (!this.map) return;
    const url = layer.endpoints.features!;
    let data: Record<string, unknown>;
    try {
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      data = (await res.json()) as Record<string, unknown>;
    } catch (err) {
      console.warn('[MapLibreAdapter] features endpoint fetch failed', url, err);
      if (layer.bbox) this.addVectorBboxFallback(sourceId, layerId, layer);
      return;
    }

    let fc: BboxFeatureCollection;
    if (data.type === 'Feature') {
      fc = { type: 'FeatureCollection', features: [data] };
    } else if (data.type === 'FeatureCollection' && Array.isArray(data.features)) {
      fc = data as BboxFeatureCollection;
    } else if (Array.isArray((data as { features?: unknown }).features)) {
      // OGC API Features JSON often omits `type` on the root object.
      fc = {
        type: 'FeatureCollection',
        features: (data as { features: BboxFeatureCollection['features'] }).features,
      };
    } else {
      console.warn('[MapLibreAdapter] unexpected GeoJSON root type', data.type);
      if (layer.bbox) this.addVectorBboxFallback(sourceId, layerId, layer);
      return;
    }

    fc = (await transformGeoJsonCoordinates(fc, layer.sourceCrsOverride, 'EPSG:4326')) as BboxFeatureCollection;

    const color = (layer.style?.['color'] as string | undefined) ?? this.vectorFallback;

    const source: GeoJSONSourceSpecification = {
      type: 'geojson',
      data: fc as unknown as GeoJSON.FeatureCollection,
      // Promote `properties.id` so queryRenderedFeatures returns stable ids.
      promoteId: 'id',
    };

    this.map.addSource(sourceId, source);
    // Cache the features for WP-4 attribute-table consumption. The
    // structural shape (`Feature[]`) matches what `getVectorFeatures`
    // returns to the chrome.
    this.vectorFeatureCache.set(layer.id, fc.features as unknown as GeoJSON.Feature[]);

    this.map.addLayer({
      id: `${layerId}-fill`,
      type: 'fill',
      source: sourceId,
      filter: ['match', ['geometry-type'], ['Polygon', 'MultiPolygon'], true, false],
      paint: {
        'fill-color': color,
        'fill-opacity': 0.2,
      },
    });
    this.registerVectorPickLayer(`${layerId}-fill`);

    this.map.addLayer({
      id: `${layerId}-line`,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': color,
        'line-width': 2,
      },
    });
    this.registerVectorPickLayer(`${layerId}-line`);

    this.map.addLayer({
      id: `${layerId}-circle`,
      type: 'circle',
      source: sourceId,
      filter: ['match', ['geometry-type'], ['Point', 'MultiPoint'], true, false],
      paint: {
        'circle-color': color,
        'circle-radius': 8,
        'circle-stroke-color': color,
        'circle-stroke-width': 1,
      },
    });
    this.registerVectorPickLayer(`${layerId}-circle`);

    const bb = bboxFromGeoJsonFeatureCollection(fc);
    if (bb && this.map) {
      const [minX, minY, maxX, maxY] = bb;
      const lonSpan = maxX - minX;
      const latSpan = maxY - minY;
      // Skip auto-zoom for near-global extents (default scene bboxes).
      if (lonSpan < 40 && latSpan < 40) {
        try {
          const epsilon = 0.0001;
          const bounds: [[number, number], [number, number]] = [
            [minX - Math.max(epsilon, lonSpan * 0.01), minY - Math.max(epsilon, latSpan * 0.01)],
            [maxX + Math.max(epsilon, lonSpan * 0.01), maxY + Math.max(epsilon, latSpan * 0.01)],
          ];
          this.map.fitBounds(
            bounds,
            { padding: 40, maxZoom: 15, duration: this.vectorOverlayMode ? 0 : 600 },
          );

          // In overlay mode Giro3D owns the camera. A source CRS correction
          // must move that primary camera too, otherwise MapLibre briefly
          // fits the corrected data and the next overlay sync moves it back.
          if (this.vectorOverlayMode && layer.sourceCrsOverride) {
            const center = this.map.getCenter();
            this.hostContext?.emit({
              type: 'cameraChange',
              camera: { lonLat: [center.lng, center.lat], zoom: this.map.getZoom() },
            });
          }
        } catch {
          /* ignore invalid bounds */
        }
      }
    }
  }

  async removeLayer(layerId: string): Promise<void> {
    if (!this.map) return;
    this.removeMapLibreArtifacts(layerId);
    this.rasterLayerIds.delete(layerId);
    this.rasterTileTemplates.delete(layerId);
    this.vectorFeatureCache.delete(layerId);
    this.layerHealth.delete(layerId);
    this.pmtilesSourceLayerResolution.delete(layerId);
  }

  private removeMapLibreArtifacts(layerId: string): void {
    if (!this.map) return;
    const lid = `layer-${layerId}`;
    const lidLine = `${lid}-line`;
    const lidFill = `${lid}-fill`;
    const lidCircle = `${lid}-circle`;
    const lidLabel = `${lid}-label`;
    const sid = `src-${layerId}`;
    this.unregisterVectorPickLayer(lidFill);
    this.unregisterVectorPickLayer(lidLine);
    this.unregisterVectorPickLayer(lidCircle);
    this.unregisterVectorPickLayer(lid);
    this.removePointSymbolLayer(layerId);
    if (this.map.getLayer(lidLabel)) this.map.removeLayer(lidLabel);
    if (this.map.getLayer(lidFill)) this.map.removeLayer(lidFill);
    if (this.map.getLayer(lidLine)) this.map.removeLayer(lidLine);
    if (this.map.getLayer(lidCircle)) this.map.removeLayer(lidCircle);
    if (this.map.getLayer(lid)) this.map.removeLayer(lid);
    if (this.map.getSource(sid)) this.map.removeSource(sid);
  }

  private async resolvePmtilesSourceLayer(
    layer: Layer,
    isContourDerivative: boolean,
  ): Promise<string | null> {
    const declared = readDeclaredPmtilesSourceLayer(layer);

    // Ground truth: the vector-layer ids the archive ACTUALLY contains. A
    // declared `source-layer` that is absent here renders nothing in MapLibre
    // (the classic "vector toggles but is blank"). Converters sometimes stamp a
    // base name while tippecanoe wrote a suffixed one (e.g. declared "Street",
    // archive "Streetintermediate"), so we reconcile rather than trust blindly.
    const available = await this.readPmtilesVectorLayerIds(layer.endpoints.pmtiles);

    const applied = this.reconcilePmtilesSourceLayer(declared, available, layer, isContourDerivative);
    this.pmtilesSourceLayerResolution.set(layer.id, { declared, applied, available });
    if (declared && applied && declared !== applied) {
      console.warn(
        `[MapLibreAdapter] source-layer "${declared}" not found in ${layer.id} archive; ` +
          `using "${applied}" instead (archive layers: ${available.join(', ') || 'unknown'}). ` +
          `Producer fix: stamp the archive's real source-layer name.`,
      );
    }
    return applied;
  }

  /**
   * Pure reconciliation of a declared PMTiles `source-layer` against the layer
   * ids actually present in the archive. Exposed (non-private) only for unit
   * tests; callers should use `resolvePmtilesSourceLayer`.
   *
   * Priority: exact archive match → fuzzy archive match (case-insensitive /
   * substring, to absorb tippecanoe suffixes) → archive's first layer → the
   * declared name (archive metadata unavailable) → kind heuristics → `features`.
   */
  reconcilePmtilesSourceLayer(
    declared: string | null,
    available: readonly string[],
    layer: Pick<Layer, 'id' | 'label'>,
    isContourDerivative: boolean,
  ): string | null {
    return reconcilePmtilesSourceLayer(declared, available, layer, isContourDerivative);
  }

  private async readPmtilesVectorLayerIds(url: string | undefined): Promise<string[]> {
    if (!url) return [];
    try {
      return [...(await readPmtilesArchiveInfo(url)).vectorLayerIds];
    } catch (err) {
      console.warn('[MapLibreAdapter] PMTiles metadata read failed', url, err);
      return [];
    }
  }

  getLayerHealth(layerId: string): 'ready' | 'loading' | 'failed' | 'unsupported' {
    return this.layerHealth.get(layerId) ?? 'loading';
  }

  /**
   * Live render diagnostics for the global Layer Properties console
   * (`LayerDiagnosticsProvider`). Returns `null` for layers this adapter has
   * never seen so the `ViewerHost` aggregator can fall through to another
   * adapter. MUST stay side-effect free.
   *
   * Layer-KIND aware only — branches off the MapLibre source/style-layer id
   * conventions, never a scene-specific layer id.
   */
  getLayerRenderDiagnostics(layerId: string): LayerRenderDiagnostics | null {
    // Health is set on the first `addLayer` and cleared on `removeLayer`, so it
    // is the cheapest "do I know this layer?" probe.
    if (!this.layerHealth.has(layerId)) return null;

    const sid = `src-${layerId}`;
    const lid = `layer-${layerId}`;
    const candidateLayerIds = [
      lid,
      `${lid}-fill`,
      `${lid}-line`,
      `${lid}-circle`,
      `${lid}-symbol`,
      `${lid}-label`,
    ];

    const map = this.map;
    const presentLayerIds = map
      ? candidateLayerIds.filter((id) => Boolean(map.getLayer(id)))
      : [];
    const hasSource = Boolean(map?.getSource(sid));
    const mounted = presentLayerIds.length > 0 || hasSource || this.rasterLayerIds.has(layerId);

    // Ground-truth applied PMTiles `source-layer`, read straight off the live
    // style layer rather than re-deriving it from metadata.
    let selectedSourceLayer: string | null = null;
    if (map) {
      for (const id of [`${lid}-fill`, `${lid}-line`, `${lid}-circle`]) {
        const styleLayer = map.getLayer(id) as { sourceLayer?: string } | undefined;
        if (styleLayer?.sourceLayer) {
          selectedSourceLayer = styleLayer.sourceLayer;
          break;
        }
      }
    }

    // Producer-side source-layer reconciliation (declared vs applied vs archive)
    // captured at add time, so the Coordinates/Source tabs can flag a mismatch.
    const resolution = this.pmtilesSourceLayerResolution.get(layerId);
    if (resolution && !selectedSourceLayer) selectedSourceLayer = resolution.applied;

    return {
      adapterId: this.id,
      renderPath: 'maplibre-overlay',
      mounted,
      // MapLibre vectors/rasters toggle by mount/unmount, so there is no
      // in-place visibility to report. `null` keeps the builder's desync check
      // honest: it still flags "store visible but not mounted" via `mounted`.
      engineVisible: null,
      engineLayerIds: hasSource ? [sid, ...presentLayerIds] : presentLayerIds,
      frameCrs: 'EPSG:3857',
      alignmentStatus: 'not-applicable',
      selectedSourceLayer,
      declaredSourceLayer: resolution?.declared ?? null,
      availableSourceLayers: resolution?.available,
      health: this.layerHealth.get(layerId) ?? 'loading',
      lastError: null,
    };
  }

  flyTo(camera: ViewerCamera): void {
    this.map?.flyTo({
      center: camera.lonLat,
      zoom: camera.zoom ?? this.map.getZoom(),
    });
  }

  /**
   * MapLibre always renders in Web Mercator. As a vector OVERLAY it follows the
   * primary 3D camera and is NOT the frame authority, so it returns `null` then
   * to let the primary engine own the viewer-level frame readout.
   */
  getActiveFrameCrs(): string | null {
    return this.vectorOverlayMode ? null : 'EPSG:3857';
  }

  private logVectorRenderState(reason: string): void {
    if (!this.vectorOverlayMode || !this.map) return;
    try {
      const layers = [...this.vectorPickLayerIds].filter((id) => this.map?.getLayer(id));
      const features = layers.length > 0 ? this.map.queryRenderedFeatures(undefined, { layers }) : [];
      const center = this.map.getCenter();
      console.info('[MapLibreAdapter] overlay render state', {
        reason,
        center: [center.lng, center.lat],
        zoom: this.map.getZoom(),
        bearing: this.map.getBearing(),
        pitch: this.map.getPitch(),
        canvas: {
          width: this.map.getCanvas().clientWidth,
          height: this.map.getCanvas().clientHeight,
        },
        layers,
        renderedFeatureCount: features.length,
      });
    } catch (err) {
      console.warn('[MapLibreAdapter] overlay render-state probe failed', err);
    }
  }

  /**
   * Keep MapLibre layers mounted and flip their layout visibility in place.
   *
   * Without this hook `ViewerHost.setLayerVisible(false)` falls back to
   * `removeLayer`, deleting the host entry for PMTiles/vector overlays. A later
   * show request can then no-op because the host no longer has a mounted layer
   * entry. This is the generic "feature layer toggles but won't come back" bug.
   */
  setLayerVisible(layerId: string, visible: boolean): void {
    this.desiredVisibility.set(layerId, visible);
    if (!this.map) return;
    const visibility = visible ? 'visible' : 'none';
    const candidates = [
      `layer-${layerId}`,
      `layer-${layerId}-fill`,
      `layer-${layerId}-line`,
      `layer-${layerId}-line-casing`,
      `layer-${layerId}-line-arrows`,
      `layer-${layerId}-circle`,
      `layer-${layerId}-symbol`,
      `layer-${layerId}-label`,
      `bbox-${layerId}`,
    ];
    for (const id of candidates) {
      try {
        if (this.map.getLayer(id)) {
          this.map.setLayoutProperty(id, 'visibility', visibility);
        }
      } catch (err) {
        console.warn('[MapLibreAdapter] setLayerVisible failed', id, err);
      }
    }
  }

  /**
   * Keep the vector OVERLAY aligned to the primary 3D camera.
   *
   * In `vectorOverlayMode` the authoritative camera lives on the primary engine
   * (Giro3D); MapLibre only paints vectors on a transparent, `pointer-events:none`
   * canvas above it and emits no `cameraChange` of its own. The shell calls this
   * on every primary-engine `cameraChange` so toggled-on PMTiles/GeoJSON vectors
   * actually render over the area in view instead of staying at the overlay's
   * mount-time default (the classic "vector toggles but nothing shows" bug).
   *
   * Because the overlay never receives pointer input and never emits camera
   * events, snapping it here can neither fight the user nor create a feedback
   * loop. Center always tracks. Zoom is taken from the event when it carries one
   * (discrete fly / zoom-to-layer); otherwise — free-orbit events from Giro3D
   * carry a camera `height` but no `zoom` — we derive a Web-Mercator zoom from
   * that height so the overlay scale tracks the 3D view. Without this the
   * overlay scale froze at its mount default and toggled-on vectors rendered at
   * the wrong scale, i.e. "vector toggles on but nothing shows in the main
   * viewer" even though the data is fine (the Preview map proves it). The
   * derivation is approximate (top-down overlay vs. a possibly-tilted 3D view,
   * assumed FOV); the goal is "vectors visible at roughly the right scale". The
   * overlay stays top-down (bearing/pitch untouched) by design.
   */
  syncOverlayCamera(camera: ViewerCamera): void {
    if (!this.vectorOverlayMode || !this.map) return;
    const lonLat = camera.lonLat;
    if (!Array.isArray(lonLat) || lonLat.length < 2) return;
    const [lon, lat] = lonLat;
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    const opts: { center: [number, number]; zoom?: number; bearing?: number; pitch?: number } = {
      center: [lon, lat],
    };
    if (typeof camera.zoom === 'number' && Number.isFinite(camera.zoom)) {
      opts.zoom = Math.max(0, Math.min(22, camera.zoom));
    } else if (typeof camera.height === 'number' && Number.isFinite(camera.height)) {
      const viewportHeightPx =
        this.map.getCanvas()?.clientHeight
        || this.map.getContainer()?.clientHeight
        || 0;
      const derived = mercatorZoomForCameraHeight({
        heightMeters: camera.height,
        latDeg: lat,
        viewportHeightPx,
      });
      if (derived != null) opts.zoom = derived;
    }
    // Track the primary 3D camera's rotation/tilt so vector features orbit and
    // tilt WITH the Giro3D scene instead of staying locked north-up & top-down.
    // Giro3D emits hpr = [headingDeg, pitchDeg, rollDeg]:
    //   heading = atan2(east, north)  → clockwise-from-north compass bearing.
    //   pitch   = asin(dz / dist)     → 0 at the horizon, -90 looking straight down.
    // MapLibre: bearing = compass direction shown at top; pitch = 0 top-down → up
    // to maxPitch toward the horizon. So maplibrePitch ≈ 90 + giro3dPitch.
    const hpr = camera.hpr;
    if (Array.isArray(hpr) && hpr.length >= 2) {
      const headingDeg = hpr[0];
      const pitchDeg = hpr[1];
      if (Number.isFinite(headingDeg)) {
        // If rotation tracks the WRONG way for your build, flip this sign.
        opts.bearing = ((headingDeg % 360) + 360) % 360;
      }
      if (Number.isFinite(pitchDeg)) {
        const maxPitch = this.map.getMaxPitch?.() ?? 60;
        opts.pitch = Math.max(0, Math.min(maxPitch, 90 + pitchDeg));
      }
    }
    try {
      this.map.jumpTo(opts);
      requestAnimationFrame(() => this.logVectorRenderState('syncOverlayCamera'));
    } catch (err) {
      console.warn('[MapLibreAdapter] syncOverlayCamera failed', err);
    }
  }

  setTime(t: Date): void {
    void t;
    this.map?.triggerRepaint();
  }

  // ---------------------------------------------------------------------------
  // WP-4 — Vector ToolCard style hooks (thin pass-through; no behavior change)
  // ---------------------------------------------------------------------------
  //
  // The three setters below mutate paint / filter / layout properties on
  // the MapLibre layers we already create for vector sources in
  // `addLayer`. None of them changes existing add/remove semantics; they
  // are no-ops when the target layer is not present, so a Vector
  // ToolCard mounted before the layer is added will not throw.

  /**
   * Update stroke / fill paint on the vector layers added for `layerId`.
   * Accepts a partial delta so callers can update only what changed.
   */
  setVectorPaint(
    layerId: string,
    delta: {
      readonly fillColor?: string;
      readonly fillOpacity?: number;
      readonly lineColor?: string;
      readonly lineWidth?: number;
      readonly lineSymbol?: LineSymbolState;
      readonly pointSize?: number;
      readonly pointSymbolId?: string;
      readonly pointRotation?: number;
      readonly polygonSymbol?: PolygonSymbolState;
      readonly uniqueValueSymbology?: UniqueValueSymbology | null;
    },
  ): void {
    const previous = this.vectorPaintById.get(layerId) ?? {};
    this.vectorPaintById.set(layerId, { ...previous, ...delta });
    this.applyStoredVectorPaint(layerId);
  }

  setVectorHighlight(
    layerId: string,
    featureId: VectorHighlightTarget,
  ): void {
    if (!isHighlightActive(featureId)) {
      this.vectorHighlightById.delete(layerId);
    } else {
      this.vectorHighlightById.set(layerId, featureId);
    }
    this.applyStoredVectorPaint(layerId);
  }

  setIdentifyCursor(armed: boolean): void {
    applyIdentifyCursor(
      [
        this.map?.getCanvas() ?? null,
        this.map?.getContainer() ?? null,
        this.hostContainer,
      ],
      armed,
    );
  }

  private applyStoredVectorPaint(layerId: string): void {
    if (!this.map) return;
    const delta = this.vectorPaintById.get(layerId) ?? {};
    const selectedId = this.vectorHighlightById.get(layerId) ?? null;
    const lineColor = delta.lineColor ?? this.vectorFallback;
    const fillColor = delta.fillColor ?? lineColor;
    const fillOpacity =
      delta.polygonSymbol?.fillMode === 'none'
        ? 0
        : delta.fillOpacity ?? 0.24;
    const lineWidth = delta.lineWidth ?? 2;
    const lineSymbol = delta.lineSymbol;
    const pointSize = delta.pointSize ?? 16;
    const pointRadius = pointSize / 2; // Convert diameter to radius for MapLibre
    const highlightOn = isHighlightActive(selectedId);
    const unique = delta.uniqueValueSymbology;

    const paintLineColor = highlightOn
      ? maplibreHighlightPaint(selectedId, VECTOR_IDENTIFY_HIGHLIGHT.lineColor, lineColor)
      : lineColor;
    const paintLineWidth = highlightOn
      ? maplibreHighlightPaint(selectedId, VECTOR_IDENTIFY_HIGHLIGHT.lineWidth, lineWidth)
      : lineWidth;
    const paintFillColor = highlightOn
      ? maplibreHighlightPaint(selectedId, VECTOR_IDENTIFY_HIGHLIGHT.fillColor, fillColor)
      : fillColor;
    const paintFillOpacity = highlightOn
      ? maplibreHighlightPaint(selectedId, VECTOR_IDENTIFY_HIGHLIGHT.fillOpacity, fillOpacity)
      : fillOpacity;
    const lineId = `layer-${layerId}-line`;
    const fillId = `layer-${layerId}-fill`;
    const circleId = `layer-${layerId}-circle`;
    const singleId = `layer-${layerId}`;
    try {
      if (this.map.getLayer(lineId)) {
        const uniqueLineColor = unique
          ? uniqueMatchExpression(
              unique,
              (entry) => entry.strokeColor,
              lineColor,
            )
          : paintLineColor;
        const uniqueLineWidth = unique
          ? uniqueMatchExpression(unique, (entry) => entry.strokeWidth, lineWidth)
          : paintLineWidth;
        this.map.setPaintProperty(lineId, 'line-color', uniqueLineColor as never);
        this.map.setPaintProperty(lineId, 'line-width', uniqueLineWidth as never);
        if (unique) {
          this.map.setPaintProperty(
            lineId,
            'line-opacity',
            uniqueMatchExpression(
              unique,
              (entry) => entry.visible ? entry.lineSymbol.opacity : 0,
              lineSymbol?.opacity ?? 1,
            ) as never,
          );
        } else if (lineSymbol || 'uniqueValueSymbology' in delta) {
          this.map.setPaintProperty(lineId, 'line-opacity', lineSymbol?.opacity ?? 1);
        }
        const dash = lineSymbol ? lineDashArray(lineSymbol.pattern) : null;
        const lineDashPaint = unique
          ? uniqueMatchExpression(
              unique,
              (entry) => ['literal', lineDashArray(entry.lineSymbol.pattern) ?? [1, 0]],
              ['literal', dash ?? [1, 0]],
            )
          : dash;
        if (lineSymbol || unique) {
          this.map.setPaintProperty(lineId, 'line-dasharray', (lineDashPaint ?? null) as never);
        }
        const layoutMap = this.map as unknown as {
          setLayoutProperty?: (id: string, property: string, value: unknown) => void;
        };
        if (lineSymbol) {
          layoutMap.setLayoutProperty?.(lineId, 'line-cap', lineSymbol.cap);
          layoutMap.setLayoutProperty?.(lineId, 'line-join', lineSymbol.join);
        }
        this.applyLineDecorations(layerId, lineId, lineSymbol);
      } else if (this.map.getLayer(singleId)) {
        this.map.setPaintProperty(singleId, 'line-color', paintLineColor as never);
        this.map.setPaintProperty(singleId, 'line-width', paintLineWidth as never);
      }
      if (this.map.getLayer(fillId)) {
        const uniquePattern = unique
          ? this.applyUniqueValuePatternImages(layerId, unique)
          : null;
        const patternId = uniquePattern
          ?? this.applyPolygonPatternImage(layerId, delta.polygonSymbol);
        this.map.setPaintProperty(
          fillId,
          'fill-pattern',
          (patternId ?? null) as never,
        );
        this.map.setPaintProperty(fillId, 'fill-color', paintFillColor as never);
        this.map.setPaintProperty(
          fillId,
          'fill-opacity',
          (unique ? 1 : paintFillOpacity) as never,
        );
      }
      const symbolId = delta.pointSymbolId ?? 'circle';
      const iconEntry = isPointIconId(symbolId) ? peekPointIcon(symbolId) : null;
      if (isPointIconId(symbolId) && !iconEntry) {
        // The icon sets are lazily imported; draw a circle for now and re-apply
        // when they arrive.
        this.schedulePointIconSetLoad(layerId, symbolId);
      }
      const shape = pointSymbolGeometryFor(symbolId).shape;
      const source: PointSymbolSource | null = iconEntry
        ? { kind: 'icon', entry: iconEntry }
        : shape === 'circle'
          ? null
          : { kind: 'shape', shape };
      // MapLibre has no shape primitive for points, so anything other than a
      // circle is drawn by a `symbol` layer with a rasterized icon. Only fall
      // back to the circle layer when that icon could not be produced.
      const symbolApplied =
        source == null
          ? false
          : this.applyPointSymbolLayer(layerId, {
              source,
              rotationDeg: pointSymbolRotationDeg(symbolId, delta.pointRotation ?? 0),
              pointSize,
              fillColor,
              selectedId: selectedId ?? null,
            });
      if (!symbolApplied) this.removePointSymbolLayer(layerId);

      if (this.map.getLayer(circleId)) {
        this.map.setPaintProperty(circleId, 'circle-color', paintFillColor as never);
        const finalRadius = highlightOn
          ? maplibreHighlightPaint(selectedId, VECTOR_IDENTIFY_HIGHLIGHT.pointRadius, pointRadius)
          : pointRadius;
        this.map.setPaintProperty(circleId, 'circle-radius', finalRadius as never);
        // Opacity (not visibility) hides the circle behind a custom symbol —
        // `setLayerVisibility` owns the visibility key and would undo it.
        const circleOpacity = symbolApplied ? 0 : 1;
        this.map.setPaintProperty(circleId, 'circle-opacity', circleOpacity as never);
        this.map.setPaintProperty(circleId, 'circle-stroke-opacity', circleOpacity as never);
      }
    } catch (err) {
      console.warn('[MapLibreAdapter] applyStoredVectorPaint failed for', layerId, err);
    }
  }

  private applyLineDecorations(
    layerId: string,
    lineId: string,
    symbol: LineSymbolState | undefined,
  ): void {
    if (!this.map) return;
    const casingId = `layer-${layerId}-line-casing`;
    const arrowId = `layer-${layerId}-line-arrows`;
    const lineLayer = this.map.getLayer(lineId) as unknown as {
      source?: string;
      sourceLayer?: string;
      filter?: unknown;
      minzoom?: number;
      maxzoom?: number;
    } | undefined;
    const layoutMap = this.map as unknown as {
      setLayoutProperty?: (id: string, property: string, value: unknown) => void;
    };

    if (symbol?.pattern === 'cased' && lineLayer?.source) {
      if (!this.map.getLayer(casingId)) {
        this.map.addLayer(
          {
            id: casingId,
            type: 'line',
            source: lineLayer.source,
            ...(lineLayer.sourceLayer ? { 'source-layer': lineLayer.sourceLayer } : {}),
            ...(lineLayer.filter ? { filter: lineLayer.filter as never } : {}),
            ...(lineLayer.minzoom != null ? { minzoom: lineLayer.minzoom } : {}),
            ...(lineLayer.maxzoom != null ? { maxzoom: lineLayer.maxzoom } : {}),
            paint: {},
          },
          lineId,
        );
      }
      this.map.setPaintProperty(casingId, 'line-color', symbol.casingColor);
      this.map.setPaintProperty(
        casingId,
        'line-width',
        symbol.width + symbol.casingWidth * 2,
      );
      this.map.setPaintProperty(casingId, 'line-opacity', symbol.opacity);
      layoutMap.setLayoutProperty?.(casingId, 'line-cap', symbol.cap);
      layoutMap.setLayoutProperty?.(casingId, 'line-join', symbol.join);
      layoutMap.setLayoutProperty?.(casingId, 'visibility', 'visible');
    } else if (this.map.getLayer(casingId)) {
      layoutMap.setLayoutProperty?.(casingId, 'visibility', 'none');
    }

    if (symbol?.pattern === 'arrow' && lineLayer?.source) {
      if (!this.map.getLayer(arrowId)) {
        this.map.addLayer({
          id: arrowId,
          type: 'symbol',
          source: lineLayer.source,
          ...(lineLayer.sourceLayer ? { 'source-layer': lineLayer.sourceLayer } : {}),
          ...(lineLayer.filter ? { filter: lineLayer.filter as never } : {}),
          layout: {
            'symbol-placement': 'line',
            'symbol-spacing': 80,
            'text-field': '▶',
            'text-size': 14,
            'text-rotation-alignment': 'map',
            'text-keep-upright': false,
          },
          paint: {
            'text-color': symbol.color,
            'text-opacity': symbol.opacity,
          },
        });
      }
      this.map.setPaintProperty(arrowId, 'text-color', symbol.color);
      this.map.setPaintProperty(arrowId, 'text-opacity', symbol.opacity);
      layoutMap.setLayoutProperty?.(arrowId, 'visibility', 'visible');
    } else if (this.map.getLayer(arrowId)) {
      layoutMap.setLayoutProperty?.(arrowId, 'visibility', 'none');
    }
  }

  private applyPolygonPatternImage(
    layerId: string,
    symbol: PolygonSymbolState | undefined,
  ): string | null {
    if (!this.map) return null;
    const uniqueIds = this.uniquePatternImagesByLayer.get(layerId);
    if (uniqueIds) {
      for (const id of uniqueIds) {
        if (this.map.hasImage?.(id)) {
          try {
            this.map.removeImage(id);
          } catch {
            /* style may be changing */
          }
        }
      }
      this.uniquePatternImagesByLayer.delete(layerId);
    }
    const previousId = this.polygonPatternImageByLayer.get(layerId);
    if (!symbol || symbol.fillMode === 'solid' || symbol.fillMode === 'none') {
      if (previousId && this.map.hasImage?.(previousId)) {
        try {
          this.map.removeImage(previousId);
        } catch {
          /* style may be changing */
        }
      }
      this.polygonPatternImageByLayer.delete(layerId);
      return null;
    }
    const id = `gv-polygon-${layerId}-${hashString(polygonPatternKey(symbol))}`;
    if (previousId === id && this.map.hasImage?.(id)) return id;
    const canvas = createPolygonPatternCanvas(symbol);
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return null;
    if (!this.map.hasImage?.(id)) {
      this.map.addImage(id, context.getImageData(0, 0, canvas.width, canvas.height));
    }
    if (previousId && previousId !== id && this.map.hasImage?.(previousId)) {
      try {
        this.map.removeImage(previousId);
      } catch {
        /* style may be changing */
      }
    }
    this.polygonPatternImageByLayer.set(layerId, id);
    return id;
  }

  private applyUniqueValuePatternImages(
    layerId: string,
    unique: UniqueValueSymbology,
  ): readonly unknown[] | null {
    if (!this.map || unique.classes.length === 0) return null;
    const singleId = this.polygonPatternImageByLayer.get(layerId);
    if (singleId && this.map.hasImage?.(singleId)) {
      try {
        this.map.removeImage(singleId);
      } catch {
        /* style may be changing */
      }
    }
    this.polygonPatternImageByLayer.delete(layerId);
    const nextIds = new Set<string>();
    const values: unknown[] = ['match', ['get', unique.field]];
    for (const entry of unique.classes) {
      const symbol = entry.visible
        ? {
            ...entry.polygonSymbol,
            backgroundColor: entry.fillColor,
          }
        : {
            ...entry.polygonSymbol,
            fillMode: 'none' as const,
          };
      const id = `gv-unique-${layerId}-${hashString(`${entry.id}:${polygonPatternKey(symbol)}:${entry.fillOpacity}`)}`;
      nextIds.add(id);
      if (!this.map.hasImage?.(id)) {
        const canvas = createPolygonPatternCanvas(symbol, entry.fillOpacity);
        const context = canvas?.getContext('2d');
        if (canvas && context) {
          this.map.addImage(id, context.getImageData(0, 0, canvas.width, canvas.height));
        }
      }
      values.push(entry.value, id);
    }
    const fallback = [...nextIds][0];
    if (!fallback) return null;
    values.push(fallback);

    const previous = this.uniquePatternImagesByLayer.get(layerId) ?? new Set<string>();
    for (const id of previous) {
      if (!nextIds.has(id) && this.map.hasImage?.(id)) {
        try {
          this.map.removeImage(id);
        } catch {
          /* style may be changing */
        }
      }
    }
    this.uniquePatternImagesByLayer.set(layerId, nextIds);
    return values;
  }

  /**
   * Add (or update) the `symbol` layer that draws non-circle point symbols.
   * Returns `false` when the icon could not be rasterized or the source is not
   * mounted, so the caller can keep the plain circle rendering.
   */
  private applyPointSymbolLayer(
    layerId: string,
    options: {
      readonly source: PointSymbolSource;
      readonly rotationDeg: number;
      readonly pointSize: number;
      readonly fillColor: string;
      readonly selectedId: VectorHighlightTarget;
    },
  ): boolean {
    if (!this.map) return false;
    const iconId = this.ensurePointSymbolImage(
      layerId,
      options.source,
      options.fillColor,
      '#ffffff',
    );
    if (!iconId) return false;
    const symbolLayerId = `layer-${layerId}-symbol`;
    if (!this.map.getLayer(symbolLayerId)) {
      const circle = this.map.getLayer(`layer-${layerId}-circle`) as
        | { source?: string; sourceLayer?: string }
        | undefined;
      const sourceId = circle?.source ?? `src-${layerId}`;
      if (!this.map.getSource(sourceId)) return false;
      const spec: Record<string, unknown> = {
        id: symbolLayerId,
        type: 'symbol',
        source: sourceId,
        filter: ['match', ['geometry-type'], ['Point', 'MultiPoint'], true, false],
        layout: {
          'icon-image': iconId,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      };
      if (circle?.sourceLayer) spec['source-layer'] = circle.sourceLayer;
      this.map.addLayer(spec as never);
      this.registerVectorPickLayer(symbolLayerId);
    }

    const highlightOn = isHighlightActive(options.selectedId);
    const highlightIconId = highlightOn
      ? this.ensurePointSymbolImage(
          layerId,
          options.source,
          VECTOR_IDENTIFY_HIGHLIGHT.fillColor,
          VECTOR_IDENTIFY_HIGHLIGHT.lineColor,
        )
      : null;
    const iconImage =
      highlightOn && highlightIconId
        ? maplibreHighlightPaint(options.selectedId, highlightIconId, iconId)
        : iconId;
    // Icons are rasterized once at a fixed edge length and scaled here, so
    // dragging the size control never re-uploads a bitmap.
    const iconScale = options.pointSize / POINT_SYMBOL_RASTER_PX;
    const iconSize = highlightOn
      ? maplibreHighlightPaint(
          options.selectedId,
          (Math.max(options.pointSize, VECTOR_IDENTIFY_HIGHLIGHT.pointRadius * 2) * 1.35) /
            POINT_SYMBOL_RASTER_PX,
          iconScale,
        )
      : iconScale;
    this.map.setLayoutProperty(symbolLayerId, 'icon-image', iconImage as never);
    this.map.setLayoutProperty(symbolLayerId, 'icon-size', iconSize as never);
    this.map.setLayoutProperty(symbolLayerId, 'icon-rotate', options.rotationDeg as never);
    return true;
  }

  /**
   * Upload the bitmap for a symbol/colour pair once and return its image id, or
   * `null` when it is not ready. Built-in shapes rasterize synchronously from a
   * canvas path; registry icons must decode an SVG first, so they return `null`
   * on the call that starts the decode and re-apply the paint when it lands.
   */
  private ensurePointSymbolImage(
    layerId: string,
    source: PointSymbolSource,
    fillColor: string,
    strokeColor: string,
  ): string | null {
    if (!this.map) return null;
    const iconId =
      source.kind === 'icon'
        ? `gv-icon-${source.entry.id}-${fillColor}`
        : `gv-point-${source.shape}-${fillColor}-${strokeColor}`;
    if (this.pointSymbolImageIds.has(iconId)) return iconId;
    try {
      if (this.map.hasImage?.(iconId)) {
        this.pointSymbolImageIds.add(iconId);
        return iconId;
      }
      if (source.kind === 'icon') {
        this.schedulePointIconImage(layerId, iconId, source.entry, fillColor);
        return null;
      }
      const raster = rasterizePointSymbol({ shape: source.shape, fillColor, strokeColor });
      if (!raster) return null;
      this.map.addImage(iconId, raster);
      this.pointSymbolImageIds.add(iconId);
      return iconId;
    } catch (err) {
      console.warn('[MapLibreAdapter] point symbol image failed for', iconId, err);
      return null;
    }
  }

  /** Decode a registry icon into an image, then re-apply the layer's paint. */
  private schedulePointIconImage(
    layerId: string,
    iconId: string,
    entry: PointIconEntry,
    fillColor: string,
  ): void {
    if (this.pendingPointIconImages.has(iconId)) return;
    this.pendingPointIconImages.add(iconId);
    void rasterizeSvgPointIcon(pointIconDataUrl(entry, fillColor))
      .then((raster) => {
        this.pendingPointIconImages.delete(iconId);
        if (!raster || !this.map) return;
        if (!this.map.hasImage?.(iconId)) this.map.addImage(iconId, raster);
        this.pointSymbolImageIds.add(iconId);
        // The synchronous path now finds the image, so this settles.
        this.applyStoredVectorPaint(layerId);
      })
      .catch((err) => {
        this.pendingPointIconImages.delete(iconId);
        console.warn('[MapLibreAdapter] point icon rasterize failed for', iconId, err);
      });
  }

  /** Import the lazily-loaded icon sets, then re-apply the layer's paint. */
  private schedulePointIconSetLoad(layerId: string, symbolId: string): void {
    const key = `${layerId}|${symbolId}`;
    if (this.pendingPointIconSets.has(key)) return;
    this.pendingPointIconSets.add(key);
    void loadPointIcons()
      .then(() => {
        this.pendingPointIconSets.delete(key);
        this.applyStoredVectorPaint(layerId);
      })
      .catch(() => {
        this.pendingPointIconSets.delete(key);
      });
  }

  private removePointSymbolLayer(layerId: string): void {
    if (!this.map) return;
    const symbolLayerId = `layer-${layerId}-symbol`;
    this.unregisterVectorPickLayer(symbolLayerId);
    try {
      if (this.map.getLayer(symbolLayerId)) this.map.removeLayer(symbolLayerId);
    } catch {
      /* layer may already be gone with its source */
    }
  }

  /**
   * Apply or clear a MapLibre filter expression on the vector layers
   * added for `layerId`. Passing `null` removes the filter.
   */
  setVectorFilter(layerId: string, filter: FilterSpecification | null): void {
    if (!this.map) return;
    const candidates = [
      `layer-${layerId}-line`,
      `layer-${layerId}-fill`,
      `layer-${layerId}`,
    ];
    for (const id of candidates) {
      try {
        if (this.map.getLayer(id)) {
          this.map.setFilter(id, filter);
        }
      } catch (err) {
        console.warn('[MapLibreAdapter] setVectorFilter failed', id, err);
      }
    }
  }

  /**
   * Toggle text labels on the vector layer. When enabled with a non-
   * null field, adds (idempotently) a `symbol` layer that reads
   * `['get', field]`; when disabled, removes the symbol layer.
   */
  setVectorLabels(
    layerId: string,
    options: { readonly enabled: boolean; readonly field: string | null },
  ): void {
    if (!this.map) return;
    const labelId = `layer-${layerId}-label`;
    const sourceId = `src-${layerId}`;
    try {
      if (!options.enabled || !options.field) {
        if (this.map.getLayer(labelId)) this.map.removeLayer(labelId);
        return;
      }
      if (!this.map.getSource(sourceId)) return;
      if (this.map.getLayer(labelId)) this.map.removeLayer(labelId);
      this.map.addLayer({
        id: labelId,
        type: 'symbol',
        source: sourceId,
        layout: {
          'text-field': ['to-string', ['get', options.field]],
          'text-size': 12,
          'text-anchor': 'top',
          'text-offset': [0, 0.5],
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': this.canvasLight,
          'text-halo-color': this.vectorFallback,
          'text-halo-width': 1,
        },
      });
    } catch (err) {
      console.warn('[MapLibreAdapter] setVectorLabels failed', err);
    }
  }

  /**
   * Return the in-memory GeoJSON features held by the GeoJSON source
   * for `layerId`. Returns an empty array for non-GeoJSON sources
   * (PMTiles features are not queryable until they are visible at the
   * current viewport — out of scope for WP-4).
   */
  getVectorFeatures(layerId: string): ReadonlyArray<VectorExportFeature> {
    const features = this.vectorFeatureCache.get(layerId) ?? [];
    return features.map((feature) => {
      const props = (feature.properties ?? {}) as Record<string, unknown>;
      return {
        id: (feature.id ?? props.id) as string | number | undefined,
        properties: props as Readonly<Record<string, unknown>>,
        geometry: feature.geometry,
      };
    });
  }

  zoomToVectorBounds(bounds: readonly [number, number, number, number]): void {
    if (!this.map || !bounds.every(Number.isFinite)) return;
    const [minX, minY, maxX, maxY] = bounds;
    try {
      this.map.fitBounds(
        [
          [minX, minY],
          [maxX, maxY],
        ],
        { padding: 64, maxZoom: 17, duration: 600 },
      );
    } catch (err) {
      console.warn('[MapLibreAdapter] zoomToVectorBounds failed', err);
    }
  }

  setVectorEditMode(
    layerId: string,
    tool: VectorEditTool | null,
    geometryKind: IdentifyGeometryKind = 'unknown',
    features?: ReadonlyArray<ScaleSourceFeature>,
    selectedFeatureId?: string | number | null,
    onEdit?: (result: VectorEditResult) => void,
  ): void {
    if (!this.map || !tool) {
      this.stopTerraDraw();
      this.stopScaleMode();
      this.hydrateSessionOverlayFromStore(layerId);
      return;
    }
    if (tool === 'scale') {
      this.stopTerraDraw();
      this.startScaleMode(layerId, features, selectedFeatureId);
      this.hydrateSessionOverlayFromStore(layerId);
      return;
    }
    this.stopScaleMode();
    this.hydrateSessionOverlayFromStore(layerId);
    const session = resolveVectorEditSession(tool, geometryKind);
    if (!session) {
      this.deleteSelectedVectorFeature(layerId);
      return;
    }
    this.applyEditPointerChrome(tool);
    try {
      this.ensureTerraDraw(session);
      this.terraDrawLayerId = layerId;
      this.terraDrawTool = tool;
      this.terraDrawOnEdit = onEdit ?? null;
      if (session.terraMode === 'select') {
        this.setVectorStyleLayersVisible(layerId, false);
        this.loadFeaturesIntoTerraDraw(layerId, features, selectedFeatureId);
      } else {
        this.setVectorStyleLayersVisible(layerId, true);
        this.loadFeaturesIntoTerraDraw(
          layerId,
          tool === 'trace' ? features : null,
          null,
        );
      }
      this.terraDraw?.setMode(session.terraMode);
    } catch (err) {
      console.warn('[MapLibreAdapter] setVectorEditMode failed', err);
    }
  }

  setVectorEditCursor(cursor: string): void {
    applyOverlayEditCapture(this.editPointerElements(), {
      armed: Boolean(cursor),
      cursor,
      overlay: this.vectorOverlayMode,
    });
  }

  deleteSelectedVectorFeature(
    layerId: string,
    selectedFeatureId?: string | number | null,
    onEdit?: (result: VectorEditResult) => void,
  ): void {
    const callback = onEdit ?? this.terraDrawOnEdit;
    if (!this.terraDraw) {
      if (selectedFeatureId != null) {
        const result = { operation: 'delete' as const, featureId: selectedFeatureId };
        this.applyEditResultToMutableSource(layerId, result);
        callback?.(result);
      }
      return;
    }
    try {
      const snapshot = this.terraDraw.getSnapshot() as Array<GeoJSON.Feature & { id?: string }>;
      const selected = snapshot.filter((feature) => feature.properties?.selected === true);
      const matched =
        selectedFeatureId == null
          ? []
          : snapshot.filter((feature) => String(feature.id) === String(selectedFeatureId));
      const victims = selected.length > 0 ? selected : matched;
      const toRemove = victims
        .map((feature) => feature.id)
        .filter((id): id is string => typeof id === 'string');
      if (toRemove.length > 0) {
        this.terraDraw.removeFeatures(toRemove);
        for (const id of toRemove) {
          const result = { operation: 'delete' as const, featureId: id };
          this.applyEditResultToMutableSource(layerId, result);
          callback?.(result);
        }
      } else if (selectedFeatureId != null) {
        const result = { operation: 'delete' as const, featureId: selectedFeatureId };
        this.applyEditResultToMutableSource(layerId, result);
        callback?.(result);
      }
    } catch (err) {
      console.warn('[MapLibreAdapter] deleteSelectedVectorFeature failed', err);
    }
  }

  /**
   * Drop one feature from the persistent working-copy overlay, the mutable
   * GeoJSON source, and the live Terra Draw sketch. Used by the ToolCard's
   * per-polygon delete row, which names the victim explicitly instead of
   * relying on whatever the engine currently considers "selected".
   */
  removeVectorSessionFeature(layerId: string, featureId: string | number): void {
    const key = String(featureId);
    if (!key) return;
    const overlay = this.vectorEditOverlayFeatures.get(layerId);
    overlay?.delete(key);
    this.terraDrawSeedIds.delete(key);
    try {
      this.terraDraw?.removeFeatures([key]);
    } catch {
      /* the id may not exist in the current sketch */
    }
    this.applyEditResultToMutableSource(layerId, {
      operation: 'delete',
      featureId,
    });
    this.renderSessionOverlay(layerId);
  }

  private ensureTerraDraw(options: { readonly snap: boolean; readonly scale: boolean }): void {
    if (
      this.terraDraw &&
      this.terraDrawOptions &&
      this.terraDrawOptions.snap === options.snap &&
      this.terraDrawOptions.scale === options.scale
    ) {
      return;
    }
    this.stopTerraDraw(false);
    if (!this.map) return;
    const draw = createLayerTerraDraw(this.map, options);
    draw.start();
    draw.on('finish', (id, context) => {
      if (this.terraDrawTool === 'modify' || context.action !== 'draw') {
        this.emitChangedTerraDrawFeatures([String(id)]);
        return;
      }
      this.emitFinishedTerraDrawFeature(String(id));
    });
    draw.on('change', (changedIds) => {
      if (this.terraDrawTool === 'modify') {
        this.emitChangedTerraDrawFeatures(
          Array.isArray(changedIds) ? changedIds.map(String) : [],
        );
      }
    });
    this.terraDraw = draw;
    this.terraDrawOptions = { snap: options.snap, scale: options.scale };
  }

  private loadFeaturesIntoTerraDraw(
    layerId: string,
    incoming?: ReadonlyArray<ScaleSourceFeature> | null,
    selectedFeatureId?: string | number | null,
  ): void {
    if (!this.terraDraw) return;
    const cached: ReadonlyArray<ScaleSourceFeature> =
      incoming === null
        ? []
        : incoming
      ?? (this.vectorFeatureCache.get(layerId) ?? []).map((feature) => ({
        id:
          typeof feature.id === 'string' || typeof feature.id === 'number'
            ? feature.id
            : undefined,
        properties: feature.properties ?? {},
        geometry: feature.geometry,
      }));
    const normalized = scaleFeaturesFrom(cached);
    const selected =
      selectedFeatureId == null
        ? normalized
        : normalized.filter((feature) => {
            const id = feature.id ?? feature.properties?.id;
            return id != null && String(id) === String(selectedFeatureId);
          });
    const source = selected.length > 0 ? selected : normalized;
    const features = source
      .filter((feature) => feature.geometry)
      .map((feature, index) => ({
        type: 'Feature' as const,
        id: String(feature.id ?? feature.properties?.id ?? `edit-${index}`),
        geometry: feature.geometry,
        properties: {
          ...(feature.properties ?? {}),
          mode: terraModeFromGeometry(feature.geometry),
        },
      }));
    try {
      this.terraDraw.clear();
      this.terraDrawSeedIds = new Set(features.map((feature) => String(feature.id)));
      if (features.length > 0) {
        this.terraDraw.addFeatures(
          features as unknown as Parameters<TerraDraw['addFeatures']>[0],
        );
      }
    } catch (err) {
      console.warn('[MapLibreAdapter] loadFeaturesIntoTerraDraw failed', err);
    }
  }

  private emitFinishedTerraDrawFeatures(): void {
    if (!this.terraDraw || !this.terraDrawOnEdit) return;
    const snapshot = this.terraDraw.getSnapshot() as GeoJSON.Feature[];
    for (const feature of snapshot) {
      const id = String(feature.id ?? '');
      if (!id || this.terraDrawSeedIds.has(id)) continue;
      this.emitFinishedTerraDrawFeature(id, feature);
    }
  }

  private emitFinishedTerraDrawFeature(
    id: string,
    snapshotFeature?: GeoJSON.Feature,
  ): void {
    if (
      !this.terraDraw
      || !this.terraDrawOnEdit
      || !id
      || this.terraDrawSeedIds.has(id)
    ) {
      return;
    }
    const feature =
      snapshotFeature
      ?? this.terraDraw.getSnapshotFeature(id) as GeoJSON.Feature | undefined;
    const completed = feature ? completedPolygonFeature(feature) : null;
    if (!completed) return;
    this.terraDrawSeedIds.add(id);
    const result: VectorEditResult = {
      operation: 'create',
      feature: cleanTerraDrawFeature(completed),
    };
    if (this.terraDrawLayerId) {
      this.applyEditResultToMutableSource(this.terraDrawLayerId, result);
    }
    this.terraDrawOnEdit(result);
  }

  private emitChangedTerraDrawFeatures(changedIds: readonly string[]): void {
    if (!this.terraDraw || !this.terraDrawOnEdit) return;
    const wanted = new Set(changedIds);
    const snapshot = this.terraDraw.getSnapshot() as GeoJSON.Feature[];
    for (const feature of snapshot) {
      const id = String(feature.id ?? '');
      if (
        !id
        || !this.terraDrawSeedIds.has(id)
        || (wanted.size > 0 && !wanted.has(id))
      ) {
        continue;
      }
      const result: VectorEditResult = {
        operation: 'update',
        feature: cleanTerraDrawFeature(feature),
      };
      if (this.terraDrawLayerId) {
        this.applyEditResultToMutableSource(this.terraDrawLayerId, result);
      }
      this.terraDrawOnEdit(result);
    }
  }

  private applyEditResultToMutableSource(
    layerId: string,
    result: VectorEditResult,
  ): void {
    if (!this.map) return;
    const source = this.map.getSource(`src-${layerId}`) as
      | { setData?: (data: GeoJSON.FeatureCollection) => void }
      | undefined;
    if (typeof source?.setData !== 'function') {
      this.applyEditResultToOverlaySource(layerId, result);
      return;
    }
    const current = this.vectorFeatureCache.get(layerId) ?? [];
    const wanted = String(result.featureId ?? result.feature?.id ?? '');
    let next = current;
    if (result.operation === 'delete') {
      next = current.filter((feature) => String(feature.id ?? feature.properties?.id) !== wanted);
    } else if (result.feature) {
      const edited: GeoJSON.Feature = {
        type: 'Feature',
        id: result.feature.id,
        properties: { ...(result.feature.properties ?? {}) },
        geometry: result.feature.geometry as GeoJSON.Geometry,
      };
      const index = current.findIndex(
        (feature) => String(feature.id ?? feature.properties?.id) === wanted,
      );
      next =
        index >= 0
          ? current.map((feature, featureIndex) => featureIndex === index ? edited : feature)
          : [...current, edited];
    }
    this.vectorFeatureCache.set(layerId, next);
    source.setData({ type: 'FeatureCollection', features: next });
  }

  private applyEditResultToOverlaySource(
    layerId: string,
    result: VectorEditResult,
  ): void {
    if (!this.map) return;
    const features =
      this.vectorEditOverlayFeatures.get(layerId)
      ?? new Map<string, GeoJSON.Feature>();
    this.vectorEditOverlayFeatures.set(layerId, features);
    const key = String(result.featureId ?? result.feature?.id ?? '');
    if (!key) return;
    if (result.operation === 'delete') {
      features.delete(key);
    } else if (result.feature?.geometry) {
      features.set(key, {
        type: 'Feature',
        id: result.feature.id,
        properties: { ...(result.feature.properties ?? {}) },
        geometry: result.feature.geometry as GeoJSON.Geometry,
      });
    }
    this.renderSessionOverlay(layerId);
  }

  /**
   * Draw the persistent working-copy overlay for `layerId`. Called after every
   * commit and on every `setVectorEditMode` entry so a polygon that was drawn
   * during Draw survives switching to Scale, Modify, or Trace — no matter what
   * MapLibre style refresh happens in between. The overlay is always moved to
   * the top of the layer stack so nothing painted after it can hide it.
   */
  private renderSessionOverlay(layerId: string): void {
    if (!this.map) return;
    const features =
      this.vectorEditOverlayFeatures.get(layerId)
      ?? new Map<string, GeoJSON.Feature>();
    this.vectorEditOverlayFeatures.set(layerId, features);
    const sourceId = `vector-edit-session-${layerId}`;
    if (!this.map.getSource(sourceId)) {
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }
    const paint = this.vectorPaintById.get(layerId);
    const fillLayerId = `${sourceId}-fill`;
    if (!this.map.getLayer(fillLayerId)) {
      this.map.addLayer({
        id: fillLayerId,
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': paint?.fillColor ?? '#ff8a80',
          'fill-opacity': paint?.fillOpacity ?? 0.45,
        },
      });
    }
    const lineLayerId = `${sourceId}-line`;
    if (!this.map.getLayer(lineLayerId)) {
      this.map.addLayer({
        id: lineLayerId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': paint?.lineColor ?? '#ff554d',
          'line-width': paint?.lineWidth ?? 3,
        },
      });
    }
    const overlaySource = this.map.getSource(sourceId) as
      | { setData?: (data: GeoJSON.FeatureCollection) => void }
      | undefined;
    overlaySource?.setData?.({
      type: 'FeatureCollection',
      features: [...features.values()],
    });
    try {
      if (this.map.getLayer(fillLayerId)) this.map.moveLayer(fillLayerId);
      if (this.map.getLayer(lineLayerId)) this.map.moveLayer(lineLayerId);
    } catch {
      /* moveLayer throws when maplibre style is not fully hydrated yet */
    }
  }

  /**
   * Copy every persistent session feature for `layerId` from the session
   * store into `vectorEditOverlayFeatures`, then repaint. Called whenever
   * the user switches edit tools so the overlay is guaranteed to reflect
   * every polygon the user has committed this session.
   */
  private hydrateSessionOverlayFromStore(layerId: string): void {
    if (!this.map) return;
    const persisted = getVectorSessionFeaturesForLayer(layerId);
    if (persisted.length === 0) {
      if (!this.vectorEditOverlayFeatures.has(layerId)) return;
      this.renderSessionOverlay(layerId);
      return;
    }
    const features =
      this.vectorEditOverlayFeatures.get(layerId)
      ?? new Map<string, GeoJSON.Feature>();
    for (const entry of persisted) {
      const id = entry.feature.id ?? entry.feature.properties?.id;
      const key = id == null ? null : String(id);
      if (!key) continue;
      features.set(key, {
        type: 'Feature',
        id: entry.feature.id,
        properties: { ...(entry.feature.properties ?? {}) },
        geometry: entry.feature.geometry as GeoJSON.Geometry,
      });
    }
    this.vectorEditOverlayFeatures.set(layerId, features);
    this.renderSessionOverlay(layerId);
  }

  private setVectorStyleLayersVisible(layerId: string, visible: boolean): void {
    if (!this.map) return;
    const visibility = visible ? 'visible' : 'none';
    for (const suffix of ['-fill', '-line', '-circle', '']) {
      const id = `layer-${layerId}${suffix}`;
      try {
        if (this.map.getLayer(id)) {
          this.map.setLayoutProperty(id, 'visibility', visibility);
        }
      } catch {
        /* layer may not exist */
      }
    }
  }

  private stopTerraDraw(restoreLayers = true): void {
    const layerId = this.terraDrawLayerId;
    if (this.terraDraw) {
      try {
        if (this.terraDrawTool === 'draw' || this.terraDrawTool === 'trace') {
          this.emitFinishedTerraDrawFeatures();
        }
        this.terraDraw.stop();
      } catch {
        /* already stopped */
      }
    }
    this.terraDraw = null;
    this.terraDrawOptions = null;
    this.terraDrawLayerId = null;
    this.terraDrawTool = null;
    this.terraDrawSeedIds.clear();
    this.terraDrawOnEdit = null;
    this.applyEditPointerChrome(null);
    if (restoreLayers && layerId) this.setVectorStyleLayersVisible(layerId, true);
  }

  private applyEditPointerChrome(tool: VectorEditTool | null): void {
    applyOverlayEditCapture(this.editPointerElements(), {
      armed: Boolean(tool && tool !== 'delete'),
      cursor: editCursorForTool(tool),
      overlay: this.vectorOverlayMode,
    });
  }

  private editPointerElements(): Array<HTMLElement | null> {
    const canvas = this.map?.getCanvas() ?? null;
    return [
      canvas,
      canvas?.parentElement ?? null,
      this.map?.getContainer() ?? null,
      this.hostContainer,
    ];
  }

  private startScaleMode(
    layerId: string,
    features?: ReadonlyArray<ScaleSourceFeature>,
    selectedFeatureId?: string | number | null,
  ): void {
    this.stopScaleMode();
    this.scaleLayerId = layerId;
    this.scaleFeatures = scaleFeaturesFrom(features);
    this.applyEditPointerChrome('scale');
    this.ensureScaleLayers();
    const selected = matchScaleFeature(this.scaleFeatures, selectedFeatureId);
    if (selected?.geometry) {
      this.scaleFeature = {
        type: 'Feature',
        id: selected.id ?? 'scale-feature',
        geometry: selected.geometry as GeoJSON.Geometry,
        properties: { ...(selected.properties ?? {}) },
      };
      this.renderScaleGizmo();
    }
    console.log('[scale] armed', {
      layerId,
      featureCount: this.scaleFeatures.length,
      selectedFeatureId,
      selectedMatched: Boolean(selected?.geometry),
      overlay: this.vectorOverlayMode,
      pickLayers: this.vectorPickLayerIds.size,
    });
    try {
      this.map?.dragPan.disable();
    } catch {
      /* map may not support dragPan */
    }
    const onDown = (event: Event) => this.onScalePointerDown(event as PointerEvent);
    const onMove = (event: Event) => this.onScalePointerMove(event as PointerEvent);
    const onUp = (event: Event) => this.onScalePointerUp(event as PointerEvent);
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', onUp, true);
    this.scaleListeners = [
      { target: window, type: 'pointerdown', handler: onDown },
      { target: window, type: 'pointermove', handler: onMove },
      { target: window, type: 'pointerup', handler: onUp },
    ];
  }

  private stopScaleMode(): void {
    for (const listener of this.scaleListeners) {
      listener.target.removeEventListener(listener.type, listener.handler, true);
    }
    this.scaleListeners = [];
    this.scaleDragHandle = null;
    this.scaleFeature = null;
    this.scaleFeatures = [];
    this.scaleLayerId = null;
    this.removeScaleLayers();
    try {
      this.map?.dragPan.enable();
    } catch {
      /* map already gone */
    }
    this.applyEditPointerChrome(null);
  }

  private onScalePointerDown(event: PointerEvent): void {
    if (event.button !== 0) return;
    const mapRoot = this.hostContainer ?? this.map?.getContainer();
    const insideMap = Boolean(mapRoot && event.target instanceof Node && mapRoot.contains(event.target));
    const lngLat = this.map ? this.lngLatFromPointer(event) : { lng: NaN, lat: NaN };
    console.log('[scale] map click', {
      layerId: this.scaleLayerId,
      hasMap: Boolean(this.map),
      insideMap,
      lng: lngLat.lng,
      lat: lngLat.lat,
      clientX: event.clientX,
      clientY: event.clientY,
      cachedFeatures: this.scaleFeatures.length,
      sampleIds: this.scaleFeatures.slice(0, 5).map((feature) => feature.id),
    });
    if (!this.map || !insideMap) return;
    const handleId = this.hitScaleHandle(event);
    if (handleId && this.scaleFeature) {
      this.scaleDragHandle = handleId;
      console.info('[scale] drag handle', handleId);
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const picked = this.pickScaleFeature(event);
    if (!picked) return;
    this.scaleFeature = picked;
    console.info('[scale] picked', {
      id: picked.id,
      geometry: picked.geometry?.type,
      bbox: bboxFromGeometry(picked.geometry),
    });
    this.renderScaleGizmo();
    event.preventDefault();
    event.stopPropagation();
  }

  private onScalePointerMove(event: PointerEvent): void {
    if (!this.map || !this.scaleDragHandle || !this.scaleFeature) return;
    const lngLat = this.lngLatFromPointer(event);
    const nextGeometry = scaleGeometry(
      this.scaleFeature.geometry,
      this.scaleDragHandle,
      [lngLat.lng, lngLat.lat],
    ) as GeoJSON.Geometry;
    this.scaleFeature = { ...this.scaleFeature, geometry: nextGeometry };
    this.renderScaleGizmo();
    event.preventDefault();
  }

  private onScalePointerUp(event: PointerEvent): void {
    if (!this.scaleDragHandle) return;
    this.scaleDragHandle = null;
    this.commitScaleFeature();
    event.preventDefault();
  }

  private hitScaleHandle(event: PointerEvent): ScaleHandleId | null {
    if (!this.map || !this.scaleFeature) return null;
    const bbox = bboxFromGeometry(this.scaleFeature.geometry);
    if (!bbox) return null;
    const point = this.cssMapPointFromPointer(event);
    let nearest: { id: ScaleHandleId; dist: number } | null = null;
    for (const handle of scaleHandlesFromBbox(bbox)) {
      const projected = this.map.project([handle.lonLat[0], handle.lonLat[1]]);
      const dist = Math.hypot(projected.x - point.x, projected.y - point.y);
      if (dist <= 16 && (!nearest || dist < nearest.dist)) {
        nearest = { id: handle.id, dist };
      }
    }
    return nearest?.id ?? null;
  }

  private pickScaleFeature(event: PointerEvent): GeoJSON.Feature | null {
    if (!this.map) return null;
    const point = this.cssMapPointFromPointer(event);
    const lngLat = this.map.unproject(point);
    try {
      const overlayLayers = [SCALE_EDIT_FILL, SCALE_EDIT_LINE].filter((id) => Boolean(this.map?.getLayer(id)));
      if (overlayLayers.length > 0 && this.scaleFeature) {
        const overlayHits = this.map.queryRenderedFeatures(point, { layers: overlayLayers });
        if (overlayHits[0]?.geometry) return this.scaleFeature;
      }
    } catch {
      /* overlay layers may not be ready yet */
    }
    // ToolCard geometry first: rendered tile geometry is clipped to the tile,
    // which would give the gizmo a bbox that stops at the tile edge.
    const fromCard = pickFeatureAtLonLat(this.scaleFeatures, [lngLat.lng, lngLat.lat]);
    const rendered = fromCard ? [] : this.queryScaleLayerFeatures(point);
    console.info('[scale] pointerdown', {
      lng: lngLat.lng,
      lat: lngLat.lat,
      cachedFeatures: this.scaleFeatures.length,
      renderedHits: rendered.length,
      pickedFrom: fromCard ? 'toolcard' : rendered.length > 0 ? 'rendered' : 'none',
    });
    if (fromCard?.geometry) {
      return {
        type: 'Feature',
        id: fromCard.id ?? 'scale-feature',
        geometry: fromCard.geometry as GeoJSON.Geometry,
        properties: { ...(fromCard.properties ?? {}) },
      };
    }
    const hit = rendered[0];
    if (!hit?.geometry) return null;
    return {
      type: 'Feature',
      id: resolveFeatureIdFromRenderedFeature(hit) ?? 'scale-feature',
      geometry: hit.geometry as GeoJSON.Geometry,
      properties: { ...(hit.properties ?? {}) },
    };
  }

  private queryScaleLayerFeatures(point: maplibregl.Point): maplibregl.MapGeoJSONFeature[] {
    if (!this.map) return [];
    try {
      const layers = [...this.vectorPickLayerIds].filter((id) => Boolean(this.map?.getLayer(id)));
      if (layers.length === 0) return [];
      return this.map.queryRenderedFeatures(point, { layers }).filter((feature) => {
        const catalog = catalogLayerIdFromMapLayerId(feature.layer.id);
        return !this.scaleLayerId || catalog === this.scaleLayerId;
      });
    } catch {
      return [];
    }
  }

  /** CSS pixels vs the canvas box — same space as `map.project` / `queryRenderedFeatures`. */
  private cssMapPointFromPointer(event: PointerEvent): maplibregl.Point {
    const canvas = this.map!.getCanvas();
    const rect = canvas.getBoundingClientRect();
    return new maplibregl.Point(event.clientX - rect.left, event.clientY - rect.top);
  }

  private lngLatFromPointer(event: PointerEvent): { lng: number; lat: number } {
    return this.map!.unproject(this.cssMapPointFromPointer(event));
  }

  private ensureScaleLayers(): void {
    if (!this.map) return;
    if (!this.map.getSource(SCALE_GIZMO_SOURCE)) {
      this.map.addSource(SCALE_GIZMO_SOURCE, {
        type: 'geojson',
        data: emptyCollection(),
      });
      this.map.addLayer({
        id: SCALE_GIZMO_LINE,
        type: 'line',
        source: SCALE_GIZMO_SOURCE,
        filter: ['==', ['get', 'role'], 'box'],
        paint: {
          'line-color': '#111111',
          'line-width': 1.5,
        },
      });
      this.map.addLayer({
        id: SCALE_GIZMO_HANDLES,
        type: 'circle',
        source: SCALE_GIZMO_SOURCE,
        filter: ['==', ['get', 'role'], 'handle'],
        paint: {
          'circle-radius': 5,
          'circle-color': '#ffffff',
          'circle-stroke-color': '#111111',
          'circle-stroke-width': 1,
        },
      });
    }
    if (!this.map.getSource(SCALE_EDIT_SOURCE)) {
      this.map.addSource(SCALE_EDIT_SOURCE, {
        type: 'geojson',
        data: emptyCollection(),
      });
      this.map.addLayer({
        id: SCALE_EDIT_FILL,
        type: 'fill',
        source: SCALE_EDIT_SOURCE,
        paint: {
          'fill-color': '#22d3ee',
          'fill-opacity': 0.15,
        },
      });
      this.map.addLayer({
        id: SCALE_EDIT_LINE,
        type: 'line',
        source: SCALE_EDIT_SOURCE,
        paint: {
          'line-color': '#22d3ee',
          'line-width': 3,
        },
      });
    }
  }

  private renderScaleGizmo(): void {
    if (!this.map || !this.scaleFeature) return;
    this.ensureScaleLayers();
    const bbox = bboxFromGeometry(this.scaleFeature.geometry);
    if (!bbox) return;
    const handles = scaleHandlesFromBbox(bbox);
    const gizmo: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: { role: 'box' },
          geometry: {
            type: 'LineString',
            coordinates: bboxRing(bbox).map((coordinate) => [coordinate[0], coordinate[1]]) as Array<[number, number]>,
          },
        },
        ...handles.map((handle) => ({
          type: 'Feature' as const,
          properties: { role: 'handle', handleId: handle.id },
          geometry: { type: 'Point' as const, coordinates: [...handle.lonLat] },
        })),
      ],
    };
    const gizmoSource = this.map.getSource(SCALE_GIZMO_SOURCE) as { setData?: (data: GeoJSON.FeatureCollection) => void };
    gizmoSource?.setData?.(gizmo);
    const editSource = this.map.getSource(SCALE_EDIT_SOURCE) as { setData?: (data: GeoJSON.FeatureCollection) => void };
    editSource?.setData?.({ type: 'FeatureCollection', features: [this.scaleFeature] });
  }

  private commitScaleFeature(): void {
    if (!this.scaleFeature || !this.scaleLayerId) return;
    const layerId = this.scaleLayerId;
    const next = this.scaleFeature;
    const current = this.vectorFeatureCache.get(layerId) ?? [];
    const wanted = String(next.id ?? '');
    const replaced = current.some((feature) => String(feature.id ?? feature.properties?.id ?? '') === wanted);
    const features = replaced
      ? current.map((feature) =>
          String(feature.id ?? feature.properties?.id ?? '') === wanted ? next : feature,
        )
      : [...current, next];
    this.vectorFeatureCache.set(layerId, features);
    const source = this.map?.getSource(`src-${layerId}`) as
      | { setData?: (data: GeoJSON.FeatureCollection) => void }
      | undefined;
    source?.setData?.({ type: 'FeatureCollection', features });
    console.info('[scale] commit', {
      layerId,
      id: next.id,
      bbox: bboxFromGeometry(next.geometry),
    });
  }

  private removeScaleLayers(): void {
    if (!this.map) return;
    for (const id of [SCALE_GIZMO_HANDLES, SCALE_GIZMO_LINE, SCALE_EDIT_LINE, SCALE_EDIT_FILL]) {
      try {
        if (this.map.getLayer(id)) this.map.removeLayer(id);
      } catch {
        /* already gone */
      }
    }
    for (const id of [SCALE_GIZMO_SOURCE, SCALE_EDIT_SOURCE]) {
      try {
        if (this.map.getSource(id)) this.map.removeSource(id);
      } catch {
        /* already gone */
      }
    }
  }

  /**
   * G28 — pick the topmost rendered vector feature at a screen point.
   * Used by overlay-mode container clicks when MapLibre canvas is non-interactive.
   */
  pickVectorFeatureAtClientPoint(
    clientX: number,
    clientY: number,
  ): {
    readonly catalogLayerId: string;
    readonly featureId: string | number;
    readonly point: [number, number];
    readonly properties: Readonly<Record<string, unknown>>;
  } | null {
    if (!this.map) return null;
    const point = this.mapPointFromMouseEvent({ clientX, clientY } as MouseEvent);
    const lngLat = this.map.unproject(point);
    const hit = this.queryTopVectorFeature(point);
    if (!hit) return null;
    const props = this.lookupFeatureProperties(hit.catalogLayerId, hit.featureId);
    return {
      catalogLayerId: hit.catalogLayerId,
      featureId: hit.featureId,
      point: [lngLat.lng, lngLat.lat],
      properties: props,
    };
  }

  private registerVectorPickLayer(mapLayerId: string): void {
    this.vectorPickLayerIds.add(mapLayerId);
  }

  private unregisterVectorPickLayer(mapLayerId: string): void {
    this.vectorPickLayerIds.delete(mapLayerId);
  }

  private installVectorClickHandler(host: ViewerHostContext): void {
    if (this.containerClickHandler) return;
    this.containerClickHandler = (event: MouseEvent) => {
      if (!this.map) return;
      const point = this.mapPointFromMouseEvent(event);
      const hit = this.queryTopVectorFeature(point);
      if (hit) {
        const lngLat = this.map.unproject(point);
        host.emit({
          type: 'select',
          selection: {
            layerId: hit.catalogLayerId,
            featureId: hit.featureId,
            point: [lngLat.lng, lngLat.lat],
          },
        });
        return;
      }
      if (this.vectorOverlayMode) return;
      const canvas = this.map.getCanvas();
      const rect = canvas.getBoundingClientRect();
      if (
        event.clientX < rect.left
        || event.clientX > rect.right
        || event.clientY < rect.top
        || event.clientY > rect.bottom
      ) {
        return;
      }
      const lngLat = this.map.unproject(point);
      host.emit({
        type: 'select',
        selection: {
          layerId: '__map__',
          point: [lngLat.lng, lngLat.lat],
        },
      });
    };
    host.container.addEventListener('click', this.containerClickHandler, true);
  }

  private mapPointFromMouseEvent(event: MouseEvent): maplibregl.Point {
    const canvas = this.map!.getCanvas();
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return new maplibregl.Point(
      (event.clientX - rect.left) * scaleX,
      (event.clientY - rect.top) * scaleY,
    );
  }

  private queryTopVectorFeature(
    point: maplibregl.Point,
  ): {
    catalogLayerId: string;
    featureId: string | number;
    properties: Readonly<Record<string, unknown>>;
  } | null {
    if (!this.map || this.vectorPickLayerIds.size === 0) return null;
    const registeredLayers = [...this.vectorPickLayerIds].filter((id) => Boolean(this.map!.getLayer(id)));
    if (registeredLayers.length === 0) return null;

    const pickFromFeatures = (features: maplibregl.MapGeoJSONFeature[]) => {
      const ranked = [...features].sort((a, b) => {
        const rank = (layerId: string) => {
          if (layerId.includes('-fill')) return 0;
          if (layerId.includes('-circle') || layerId.includes('-symbol')) return 1;
          if (layerId.includes('-line')) return 3;
          return 2;
        };
        return rank(a.layer.id) - rank(b.layer.id);
      });
      for (const feature of ranked) {
        if (!registeredLayers.includes(feature.layer.id)) continue;
        const catalogLayerId = catalogLayerIdFromMapLayerId(feature.layer.id);
        const featureId = resolveFeatureIdFromRenderedFeature(feature);
        if (catalogLayerId && featureId != null) {
          const rendered = { ...(feature.properties ?? {}) } as Record<string, unknown>;
          if (rendered.id == null) rendered.id = featureId;
          const cached = this.lookupFeatureProperties(catalogLayerId, featureId);
          const properties = { ...cached, ...rendered };
          this.lastVectorPickProperties = properties;
          return { catalogLayerId, featureId, properties };
        }
      }
      return null;
    };

    try {
      let features = this.map.queryRenderedFeatures(point, { layers: registeredLayers });
      const scopedHit = pickFromFeatures(features);
      if (scopedHit) return scopedHit;
      features = this.map.queryRenderedFeatures(point);
      return pickFromFeatures(features);
    } catch {
      return null;
    }
  }

  getLastVectorPickProperties(): Readonly<Record<string, unknown>> | null {
    return this.lastVectorPickProperties;
  }

  getFeatureProperties(
    layerId: string,
    featureId: string | number,
  ): Readonly<Record<string, unknown>> {
    return this.lookupFeatureProperties(layerId, featureId);
  }

  private lookupFeatureProperties(
    catalogLayerId: string,
    featureId: string | number,
  ): Readonly<Record<string, unknown>> {
    const wanted = String(featureId);
    const cached = this.getVectorFeatures(catalogLayerId).find((feature) => {
      const id = feature.id ?? feature.properties?.id ?? feature.properties?.gml_id;
      return id != null && String(id) === wanted;
    });
    return cached?.properties ?? {};
  }

  /**
   * Optional thin pass-through used by WP-7's
   * `GeophysicsToolCard` / `BathymetryToolCard` to drive the
   * `raster-opacity` paint property of a previously-added raster
   * layer. Adapter signatures elsewhere are intentionally unchanged
   * (per VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md §8.7
   * "thin pass-through, do not change signatures").
   */
  setRasterOpacity?(layerId: string, opacity: number): void {
    if (!this.map) return;
    if (!this.rasterLayerIds.has(layerId)) return;
    const lid = `layer-${layerId}`;
    if (!this.map.getLayer(lid)) return;
    const clamped = Math.max(0, Math.min(1, opacity));
    try {
      this.map.setPaintProperty(lid, 'raster-opacity', clamped);
    } catch {
      /* ignore — paint property may not be supported on this style */
    }
  }

  /**
   * Apply the Raster ToolCard state to a mounted raster layer.
   *
   * Display sliders → MapLibre paint. Bands / color ramp / no-data → TiTiler
   * query rewrite via `source.setTiles`. Called from ToolCardDock whenever
   * the store patch changes; safe to call repeatedly.
   */
  getRasterTileTemplate(layerId: string): string | null {
    return this.rasterTileTemplates.get(layerId) ?? null;
  }

  applyRasterToolCard(
    layerId: string,
    state: {
      readonly opacity: number;
      readonly brightness: number;
      readonly contrast: number;
      readonly saturation: number;
      readonly bands: readonly [number, number, number];
      readonly colorRamp: RasterColorRamp;
      readonly renderMode?: import('@/state/useViewerStore').RasterRenderMode;
      readonly noDataMask: boolean;
    },
    options: {
      readonly multiBandImagery?: boolean;
      readonly nodata?: number | null;
      /** From `/cog/info`. Omit / 0 → paint only; never rewrite tile bands. */
      readonly bandCount?: number;
      readonly preserveColormap?: boolean;
      readonly preserveNodata?: boolean;
    } = {},
  ): void {
    if (!this.map || !this.rasterLayerIds.has(layerId)) return;
    const lid = `layer-${layerId}`;
    const sourceId = `src-${layerId}`;
    if (!this.map.getLayer(lid)) return;

    const paint = mapLibreRasterPaintFromState(state);
    try {
      this.map.setPaintProperty(lid, 'raster-opacity', paint.opacity);
      this.map.setPaintProperty(lid, 'raster-brightness-max', paint.brightnessMax);
      this.map.setPaintProperty(lid, 'raster-contrast', paint.contrast);
      this.map.setPaintProperty(lid, 'raster-saturation', paint.saturation);
    } catch {
      /* paint keys vary by MapLibre build */
    }

    // Do not rewrite the tile URL until we know the file's band count —
    // default card bands [1,2,3] on a 1-band DEM 500 every tile.
    if (!(typeof options.bandCount === 'number' && options.bandCount > 0)) return;

    const current =
      this.rasterTileTemplates.get(layerId)
      ?? (this.map.getSource(sourceId) as { tiles?: string[] } | undefined)?.tiles?.[0];
    if (!current || typeof current !== 'string') return;
    const next = applyTitilerRasterStyleToTemplate(current, state, options);
    if (next === current) return;
    const src = this.map.getSource(sourceId) as { setTiles?: (t: string[]) => void } | undefined;
    ensureShadedReliefProtocol(maplibregl);
    ensureBackgroundMaskProtocol(maplibregl);
    src?.setTiles?.([wrapRasterTileTemplateForMapLibre(next)]);
    this.rasterTileTemplates.set(layerId, next);
  }

  /**
   * Add a raster XYZ-template layer. When the URL is a TiTiler `/cog/tiles/`
   * endpoint, fetch the matching `tilejson.json` to use the COG's real bounds
   * + min/max zoom and request larger 512px tiles. This avoids 404s outside
   * the raster footprint and lets the user zoom to native pixel resolution.
   */
  private async addRasterTilesLayer(sourceId: string, layerId: string, layer: Layer): Promise<void> {
    if (!this.map) return;

    const appliedCorners = readAppliedGeoreferenceCorners(layer);
    const previewUrl =
      readGeoreference(layer)?.previewUrl
      ?? georeferencePreviewUrl(layer.endpoints.tiles);
    if (appliedCorners && previewUrl) {
      this.map.addSource(sourceId, {
        type: 'image',
        url: previewUrl,
        coordinates: appliedCorners.map((corner) => [corner[0], corner[1]]) as [[number, number], [number, number], [number, number], [number, number]],
      });
      this.map.addLayer({
        id: layerId,
        type: 'raster',
        source: sourceId,
        paint: {
          'raster-opacity': 1,
          'raster-fade-duration': 0,
        },
      });
      this.fitBoundsForLayer(layer);
      return;
    }

    const baseTiles = normalizeRasterBands(normalizeTitilerSourceHost(layer.endpoints.tiles!), layer);
    let tiles: string = baseTiles;
    // The grid stays 256 CSS px; on HiDPI the PNG behind it is 512 px so the
    // COG is resolved at physical screen resolution (see rasterTileResolution).
    const tileSize = RASTER_TILE_GRID_SIZE;
    const pixelTileSize = requestTileSize();
    let bounds: [number, number, number, number] | undefined;
    let minzoom = layer.visibility.minZoom;
    let maxzoom = layer.visibility.maxZoom;

    const tilejsonUrl = deriveTitilerTileJsonUrl(baseTiles, pixelTileSize);
    if (tilejsonUrl) {
      try {
        const res = await fetch(tilejsonUrl);
        if (res.ok) {
          const tj = (await res.json()) as {
            tiles?: string[];
            bounds?: [number, number, number, number];
            minzoom?: number;
            maxzoom?: number;
          };
          // TileJSON is for bounds/zoom only. Keep the layer's `.png` + DEM
          // stretch template — replacing with tj.tiles strips colormap and
          // defaults to JPEG (float DEM → salt-and-pepper on first open).
          if (tj.bounds && tj.bounds.length === 4) bounds = tj.bounds;
          if (typeof tj.minzoom === 'number') minzoom = minzoom ?? tj.minzoom;
          if (typeof tj.maxzoom === 'number') maxzoom = maxzoom ?? tj.maxzoom;
        }
      } catch (err) {
        console.warn('[MapLibreAdapter] tilejson fetch failed; using template URL', err);
      }
    }

    // DEM single-band COGs need rescale + colormap or they paint as JPEG
    // static / a flat gray box (QGIS auto-stretches; raw TiTiler does not).
    // Paint immediately with a provisional stretch, then refine from stats so
    // the first open matches hide→show (which remounts with a warm cache).
    // One colourise path for every single-band raster: `colorizeDemTileTemplate`
    // masks the nodata sentinel and picks the derivative role's colormap.
    // Painting even one frame through the plain gray stretch (no `nodata=`) put
    // an opaque black/white rectangle over the COG footprint.
    tiles = ensureTitilerPngTileTemplate(tiles);
    const demLike = isDemLikeLayer(layer);
    tiles = ensureTitilerPngTileTemplate(await colorizeDemTileTemplate(tiles, layer, {
      provisional: true,
    }));
    tiles = withDisplayTileSize(tiles, pixelTileSize);

    if (import.meta.env?.DEV) {
      console.info('[MapLibreAdapter] raster source configured', {
        layerId: layer.id,
        sourceId,
        tilejsonUrl,
        tileSize,
        minzoom,
        maxzoom,
        bounds,
        tiles: tiles.slice(0, 160),
      });
    }
    if (
      bounds
      && bounds[0] <= -179.999
      && bounds[1] <= -89.999
      && bounds[2] >= 179.999
      && bounds[3] >= 89.999
    ) {
      console.warn('[MapLibreAdapter] skipping raster with invalid/global TiTiler bounds', layer.id, tiles);
      return;
    }

    // Source maxzoom = native tile availability (from TiTiler tilejson).
    // Layer maxzoom must be HIGHER so MapLibre can overzoom those tiles —
    // MapLibre hides a layer when mapZoom >= layer.maxzoom (exclusive upper
    // bound). Tiny COGs often report minzoom === maxzoom (e.g. both 10); if we
    // copy that onto the layer, the raster is NEVER visible.
    const sourceMinZoom = minzoom ?? 0;
    const sourceMaxZoom = maxzoom ?? 22;
    // 24 is MapLibre's ceiling. Capping the layer at 18 used to make a raster
    // vanish once the operator zoomed past a building footprint; overzoomed
    // native tiles are still far more useful than an empty canvas.
    const layerMaxZoom = Math.min(24, Math.max(sourceMaxZoom + 6, sourceMinZoom + 1));

    this.map.addSource(sourceId, {
      type: 'raster',
      tiles: [tiles],
      tileSize,
      ...(bounds ? { bounds } : {}),
      minzoom: sourceMinZoom,
      maxzoom: sourceMaxZoom,
    });
    this.map.addLayer({
      id: layerId,
      type: 'raster',
      source: sourceId,
      minzoom: sourceMinZoom,
      maxzoom: layerMaxZoom,
      paint: {
        'raster-opacity': 1,
        'raster-resampling': rasterResamplingExpression(sourceMaxZoom),
      },
    });
    this.rasterTileTemplates.set(layer.id, tiles);

    if (demLike) {
      const provisionalTiles = tiles;
      void colorizeDemTileTemplate(provisionalTiles, layer).then((refinedTiles) => {
        if (!this.map?.getSource(sourceId)) return;
        // The ToolCard may have rewritten the template while `/cog/info` was in
        // flight. Overwriting it here dropped whatever the card had applied —
        // most visibly the background-key markers, so a border the operator had
        // just masked came back.
        if (this.rasterTileTemplates.get(layer.id) !== provisionalTiles) return;
        const refined = withDisplayTileSize(
          ensureTitilerPngTileTemplate(refinedTiles),
          pixelTileSize,
        );
        if (refined === provisionalTiles) return;
        const src = this.map.getSource(sourceId) as { setTiles?: (t: string[]) => void } | undefined;
        src?.setTiles?.([refined]);
        this.rasterTileTemplates.set(layer.id, refined);
      });
    }

    // Force a fit once tiles are configured so tiny COGs are not lost on a
    // near-white empty canvas at a mismatched zoom.
    this.fitBoundsForLayer(layer);
  }

  /**
   * If the layer has a bounding box that is not the global default, fit the
   * map view to it so the user actually sees the data instead of a thumbnail.
   */
  private fitBoundsForLayer(layer: Layer): void {
    if (!this.map) return;
    // In overlay mode the camera is owned by the primary 3D engine and applied
    // every cameraChange via syncOverlayCamera. Animating fitBounds here fought
    // that sync (an 800ms tween yanking the overlay to the layer bbox, often the
    // whole region), so the vectors briefly flew off-view. Let the sync own it.
    if (this.vectorOverlayMode) return;
    if (!layer.bbox) return;
    const [minX, minY, maxX, maxY] = layer.bbox;
    if (
      ![minX, minY, maxX, maxY].every((n) => Number.isFinite(n)) ||
      maxX <= minX ||
      maxY <= minY
    ) {
      return;
    }
    const lonSpan = maxX - minX;
    const latSpan = maxY - minY;
    if (lonSpan >= 359 || latSpan >= 169) return;
    // Tiny converter test COGs (e.g. 64×64 synthetic) only have native
    // tiles around z10 — overzooming to 18 makes them look like a blurry
    // white blob and operators think the map is broken.
    const span = Math.max(lonSpan, latSpan);
    let maxZoom = 16;
    if (span < 0.08) maxZoom = 12;
    if (span < 0.03) maxZoom = 11;
    if (span < 0.015) maxZoom = 10;
    try {
      this.map.fitBounds(
        [
          [minX, minY],
          [maxX, maxY],
        ],
        { padding: 60, maxZoom, duration: 0 },
      );
      console.info('[MapLibreAdapter] fitBoundsForLayer', {
        layerId: layer.id,
        bbox: layer.bbox,
        span,
        maxZoom,
      });
    } catch {
      /* ignore invalid bounds */
    }
  }

  private addVectorBboxFallback(sourceId: string, layerId: string, layer: Layer): void {
    if (!this.map) return;
    if (!layer.bbox) return;
    const [minX, minY, maxX, maxY] = layer.bbox;
    this.map.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [[[minX, minY], [maxX, minY], [maxX, maxY], [minX, maxY], [minX, minY]]],
        },
      },
    });
    this.map.addLayer({
      id: layerId,
      type: 'line',
      source: sourceId,
      paint: { 'line-color': this.vectorFallback, 'line-width': 2 },
    });
    // Bbox placeholders have no feature ids — exclude from vector pick.
  }
}

function hashString(value: string): string {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash).toString(36);
}

function uniqueMatchExpression(
  unique: UniqueValueSymbology,
  mapValue: (entry: UniqueValueSymbology['classes'][number]) => unknown,
  fallback: unknown,
): readonly unknown[] {
  const expression: unknown[] = ['match', ['get', unique.field]];
  for (const entry of unique.classes) expression.push(entry.value, mapValue(entry));
  expression.push(fallback);
  return expression;
}
